
import React, { useState, createContext, useContext, useEffect, useMemo, useCallback, lazy, Suspense, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import { AuthContextType, User, ToastState, Booking, Inquiry, type RefreshAdminDashboardOptions, type ToastUndoPayload } from './types';
import {
  api,
  isDatabaseAvailable,
  invalidateApiCache,
  invalidateBookingsForUser,
  invalidateAdminDashboardCache,
  setApiBearerResolver,
  LORENA_API_ACCESS_TOKEN_KEY,
  getEffectiveAdminSecret,
  LORENA_RUNTIME_ADMIN_SECRET_KEY,
  LORENA_RUNTIME_ADMIN_SECRET_EVENT,
  getQuotaBlockStatus,
  isSessionApiHardLocked,
} from './api';
import { agentDebugLog } from './utils/agentDebugLog';
import { sleep, randomJitterMs } from './utils/apiPolite';
import { getFirebaseAuth, getFirestoreDb, FIRESTORE_ARTIFACTS_APP_ID } from './src/firebase';
import { parseLocalDate } from './src/dateUtils';
import { schedulePortalChunkPrefetch } from './utils/prefetchPortal';
import { shouldDeferAdminFetch } from './utils/adminRefreshGuards';
import { mergeRosterOrKeepPrevious, applyDashboardRosterFromTeamAndClients } from './utils/adminRosterMerge';
import {
  isFirestoreBookingSoftDeleted,
  mergeClientBookingsFromServer,
  mergeEmployeeAssignedFromServer,
} from './utils/clientBookingsMerge';
import {
  normalizeBookingRowForAdminUi,
  shouldIncludeNormalizedAdminBooking,
} from "./utils/adminBookingNormalize";
import {
  mergeGlobalBookingsFromUpdateResponse,
  mergeUpsertManyIntoGlobalBookings,
  type BookingsUpdateApiResult,
} from './utils/mergeGlobalBookingsFromUpdate';
import {
  subscribePortalPeerSync,
  postPortalPeerMessage,
  getPortalTabId,
} from './utils/portalTabSync';
import { APP_BUILD_VERSION } from './constants/appBuildVersion';
import { Z_INDEX } from './constants/zIndex';
import {
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
  signInAnonymously,
} from 'firebase/auth';
import {
  setDoc,
  doc,
  getDoc,
  serverTimestamp,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  limit,
  documentId,
  type Firestore,
} from 'firebase/firestore';
import { logError } from "./utils/errorHandler";

// Lazy-load pages for faster initial load on mobile (code splitting)
const Landing = lazy(() => import('./pages/Landing'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Portal = lazy(() => import('./pages/Portal'));
const BookingPage = lazy(() => import('./pages/Booking'));
const RequestQuote = lazy(() => import('./pages/RequestQuote'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const VerifyEmailPending = lazy(() => import('./pages/VerifyEmailPending'));
const QuickPay = lazy(() => import('./pages/QuickPay'));
const BookNow = lazy(() => import('./pages/BookNow'));

export const AuthContext = createContext<AuthContextType | undefined>(undefined);
export type ShowToastOptions = { undo?: ToastUndoPayload };

export const ToastContext = createContext<
  | {
      showToast: (msg: string, type?: 'success' | 'error' | 'info', opts?: ShowToastOptions) => void;
      dismissToast: () => void;
    }
  | undefined
>(undefined);

/** Single source of truth for the admin email. Falls back to BUSINESS_EMAIL from constants. */
const ADMIN_EMAIL =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: { VITE_ADMIN_EMAIL?: string } }).env?.VITE_ADMIN_EMAIL?.toLowerCase().trim()) ||
  "lorlima83@gmail.com";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const authCtx = useContext(AuthContext);
  // Show spinner while Firebase/session is initializing (authLoading OR app DB check still pending).
  // CRITICAL: useDatabase===null means the bootstrap useEffect hasn't resolved yet — hydrateSession
  // also runs in that same window. Redirecting before it completes wipes lorena_current_user from
  // localStorage on every refresh, permanently logging the user out.
  if (authCtx?.isLoading || authCtx?.useDatabase === null) return <LoadingSpinner />;
  if (!authCtx?.user) {
    // Remove any stale session data before redirecting.
    localStorage.removeItem('lorena_current_user');
    sessionStorage.clear();
    return <Navigate to="/" replace />;
  }
  return <React.Fragment>{children}</React.Fragment>;
};

/** Dashboard vs Verification: use firebaseUser?.emailVerified as source of truth. When logged in but not verified, show verification screen. */
const LoadingSpinner: React.FC = () => (
  <div className="min-h-[50vh] flex items-center justify-center" aria-label="Loading">
    <div className="w-10 h-10 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Prevents Dashboard/data components from mounting when user is not verified. Uses firebaseUser.emailVerified as source of truth. */
const VerificationGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authCtx = useContext(AuthContext);
  const location = useLocation();
  if (authCtx?.isLoading || authCtx?.useDatabase === null) return <LoadingSpinner />;
  if (authCtx?.user && authCtx?.firebaseUser && !authCtx.firebaseUser.emailVerified) {
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    // Ignore redirect logic if we're already on the verify-email route/hash to avoid navigation loops.
    if (location.pathname !== "/verify-email" && hash !== "#/verify-email") {
      return <Navigate to="/verify-email" replace />;
    }
  }
  return <>{children}</>;
};

const VerificationGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authCtx = useContext(AuthContext);
  const { user, firebaseUser } = authCtx ?? {};
  if (user && firebaseUser && !firebaseUser.emailVerified) {
    const email = firebaseUser.email ?? user?.email ?? "your email";
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-12 bg-gradient-to-b from-stone-50/90 to-white">
        <div className="max-w-md w-full rounded-[2rem] border border-stone-200/80 bg-white/95 p-8 sm:p-10 shadow-[0_25px_50px_-15px_rgba(54,69,79,0.1)] text-center space-y-6 text-[#36454F]">
          <h2 className="text-2xl sm:text-3xl font-serif font-normal tracking-tight">Almost there!</h2>
          <p className="text-base font-medium">Please verify your email.</p>
          <p className="text-sm leading-relaxed font-medium">
            We sent a verification link to <span className="font-semibold">{email}</span>. Please click the link in that email to
            activate your account.
          </p>
          <div className="flex flex-col gap-4 pt-2">
            <button
              type="button"
              onClick={() => authCtx?.handleResendEmail()}
              disabled={(authCtx?.resendVerificationCooldownSeconds ?? 0) > 0}
              className="w-full py-3.5 rounded-2xl border-2 border-[#C9A962] text-[#36454F] font-semibold text-sm tracking-wide bg-transparent hover:bg-[#FAF6ED] disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-all"
            >
              {(authCtx?.resendVerificationCooldownSeconds ?? 0) > 0
                ? `Resend available in ${authCtx?.resendVerificationCooldownSeconds}s`
                : "Resend Verification Email"}
            </button>
            <button
              type="button"
              onClick={() => authCtx?.refreshVerificationStatus()}
              className="w-full py-2 text-sm font-medium text-[#36454F] underline underline-offset-4 decoration-[#36454F]/35 hover:decoration-[#36454F]/70 bg-transparent border-0"
            >
              Already verified? Click here to refresh
            </button>
          </div>
          <p className="text-sm font-medium text-[#36454F]/92">
            The app will automatically take you to the dashboard once you click the link in your email.
          </p>
          <p className="text-xs italic text-[#36454F]/70 leading-relaxed">
            If you don&apos;t see the email in your inbox, please check your Spam or Promotions folder.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

const defaultTeams: User[] = [
  { name: 'Team Alpha', email: 'teamalpha@lorena.com', role: 'employee', phone: '215-555-0101' },
  { name: 'Team Beta', email: 'teambeta@lorena.com', role: 'employee', phone: '215-555-0102' },
  { name: 'Team Gamma', email: 'teamgamma@lorena.com', role: 'employee', phone: '215-555-0103' },
  { name: 'Team Delta', email: 'teamdelta@lorena.com', role: 'employee', phone: '215-555-0104' },
];

/** Firestore path: /artifacts/${appId}/users/${userId}/profile (profile doc id = "default"). */
const ARTIFACTS_PROFILE_DOC_ID = "default";

/** Shown in admin Clients list when artifacts/.../users/{uid} exists but profile/default is missing or has no email. */
const PLACEHOLDER_MISSING_PROFILE_NAME = "New Client (Profile Missing)";

/** Synthetic email key so each UID maps to a unique row in registeredUsers. */
function placeholderClientForArtifactsUid(uid: string): User {
  const email = `${uid.toLowerCase()}@profile-missing.lorena`;
  return {
    name: PLACEHOLDER_MISSING_PROFILE_NAME,
    email,
    role: "client",
    emailVerified: false,
  };
}

function isInsufficientPermissionsError(e: unknown): boolean {
  const msg = (e as Error)?.message?.toLowerCase() ?? "";
  return msg.includes("permission") || msg.includes("insufficient");
}

function getErrorMessage(error: unknown, fallback = "Unknown error"): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/** Legacy-friendly: include roster row if parent user doc has no appId or it matches this app. */
function parentArtifactsUserDocIncluded(parentData: { appId?: unknown } | undefined): boolean {
  const aid = parentData?.appId;
  if (aid == null || aid === "") return true;
  const s = String(aid).trim();
  return s === FIRESTORE_ARTIFACTS_APP_ID || s === "lorena-cleaning";
}

/** Normalize Firestore role for admin roster (profile and/or parent doc); unknown → client. */
function normalizeArtifactUserRole(profileRole: unknown, parentRole?: unknown): User["role"] {
  const raw = profileRole != null && profileRole !== "" ? profileRole : parentRole;
  if (raw == null || raw === "") return "client";
  const s = String(raw).trim().toLowerCase();
  if (s === "employee" || s === "admin" || s === "client") return s;
  return "client";
}

function coalesceArtifactAppId(parentData: { appId?: unknown }, profileData?: { appId?: unknown }): string | undefined {
  const pick = (v: unknown) => (v != null && String(v).trim() !== "" ? String(v).trim() : undefined);
  return pick(profileData?.appId) ?? pick(parentData?.appId);
}

// Module-level guard: tracks which Firebase Auth UIDs have already had their
// Firestore profile ensured in this page session. Prevents ensureArtifactsProfile
// from retrying in a tight loop when Firestore returns an error (permission denied,
// network error, etc.) that would otherwise trigger the auth state observer again.
const _ensureProfileGuard = new Set<string>();

type ArtifactsParentUserDoc = { id: string; data: () => Record<string, unknown> };

/**
 * Walks artifacts/{appId}/users parent docs, loads profile/default, builds registeredUsers map.
 * Logs every skip path (wrong appId, permission errors, etc.) with uid + reason.
 */
async function buildArtifactsUsersMapFromParentDocs(
  firestoreDb: Firestore,
  appId: string,
  userDocs: ArtifactsParentUserDoc[],
  logPrefix: string
): Promise<Record<string, User>> {
  // KEY CHANGE: use UID as the map key (not email).
  // Email collisions between different UIDs no longer overwrite each other and
  // no longer emit console.warn on every snapshot rebuild, which was triggering
  // unnecessary re-renders via the console I/O side-effect chain.
  // Portal.tsx deduplicates by email when converting to `users` array via useMemo.
  const usersMap: Record<string, User> = {};

  for (const userDoc of userDocs) {
    const uid = userDoc.id;
    const parentData = userDoc.data() as { appId?: unknown; role?: unknown };

    if (!parentArtifactsUserDocIncluded(parentData)) {
      console.warn(
        `${logPrefix} Skipping uid ${uid} — appId "${String(parentData?.appId ?? "")}" not allowed`
      );
      continue;
    }

    try {
      const profileRef = doc(firestoreDb, "artifacts", appId, "users", uid, "profile", ARTIFACTS_PROFILE_DOC_ID);
      const profileSnap = await getDoc(profileRef);
      if (!profileSnap.exists()) {
        const ph = { ...placeholderClientForArtifactsUid(uid), uid };
        usersMap[uid] = ph; // keyed by UID
        continue;
      }
      const data = profileSnap.data() as {
        email?: string;
        name?: string;
        phone?: string;
        address?: string;
        role?: User["role"];
        emailVerified?: boolean;
        appId?: unknown;
      };
      const emailKey = (data.email || "").toLowerCase();
      if (!emailKey) {
        const ph = { ...placeholderClientForArtifactsUid(uid), uid };
        usersMap[uid] = ph; // keyed by UID
        continue;
      }

      usersMap[uid] = {
        uid,
        name: data.name || emailKey.split("@")[0] || "Client",
        email: emailKey,
        phone: data.phone ?? undefined,
        address: data.address ?? undefined,
        role: normalizeArtifactUserRole(data.role, parentData.role),
        emailVerified: data.emailVerified ?? false,
        appId: coalesceArtifactAppId(parentData, data),
      };
    } catch (innerErr) {
      if (isInsufficientPermissionsError(innerErr)) {
        console.warn(`${logPrefix} uid ${uid}: skipped — insufficient permissions`);
        continue;
      }
      console.warn(`${logPrefix} uid ${uid}: skipped — error:`, innerErr);
    }
  }
  return usersMap;
}

interface PermissionErrorBoundaryProps {
  firebaseUser: { emailVerified: boolean } | null;
  children: React.ReactNode;
}

class PermissionErrorBoundary extends React.Component<PermissionErrorBoundaryProps, { caught: boolean }> {
  constructor(props: PermissionErrorBoundaryProps) {
    super(props);
    this.state = { caught: false };
  }

  static getDerivedStateFromError(error: unknown): { caught: boolean } | null {
    if (isInsufficientPermissionsError(error)) return { caught: true };
    return null;
  }

  componentDidCatch(error: Error): void {
    const { firebaseUser } = this.props;
    if (isInsufficientPermissionsError(error) && firebaseUser && !firebaseUser.emailVerified) {
      if (typeof window !== "undefined") window.location.hash = "#/verify-email";
    }
  }

  render() {
    if (this.state.caught) {
      if (this.props.firebaseUser && !this.props.firebaseUser.emailVerified) {
        return (
          <div className="min-h-[40vh] flex items-center justify-center bg-slate-50" aria-live="polite">
            <p className="text-slate-500 font-medium">Redirecting…</p>
          </div>
        );
      }
      return (
        <div className="min-h-[40vh] flex items-center justify-center bg-slate-50 p-6">
          <p className="text-slate-600 font-medium">Something went wrong. Please refresh the page.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<{ uid: string; email: string | null; emailVerified: boolean } | null>(null);
  /** Admin flag from Firebase ID token custom claim; null when not loaded or no Firebase user. */
  const [firebaseAdminClaim, setFirebaseAdminClaim] = useState<boolean | null>(null);
  const [resendVerificationCooldownSeconds, setResendVerificationCooldownSeconds] = useState(0);
  const [authLoading, setAuthLoading] = useState(false);
  // null = still checking; true/false = resolved
  const [useDatabase, setUseDatabase] = useState<boolean | null>(null);
  const isInitializing = useDatabase === null;
  const [registeredUsers, setRegisteredUsers] = useState<Record<string, User>>({});
  // Single source of truth. The `bookings` view for the current user is derived via useMemo below.
  const [globalBookings, setGlobalBookings] = useState<Booking[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [bookingRequests, setBookingRequests] = useState<Booking[]>([]);
  const [bookingRequestsLoading, setBookingRequestsLoading] = useState(false);
  const [blockedDays, setBlockedDays] = useState<string[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({ message: '', isVisible: false, type: 'success' });
  const toastHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastUndoClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dbReachable, setDbReachable] = useState<boolean | null>(null);
  /** When true, Portal ignores dbReachable===false for mutation gating (localhost + health-check soft fail). */
  const [relaxPortalMutationLock, setRelaxPortalMutationLock] = useState(false);
  const [lastDbCheckAt, setLastDbCheckAt] = useState<number | null>(null);
  const [stripeWebhookLastError, setStripeWebhookLastError] = useState<{
    at: string;
    eventType?: string;
    message?: string;
  } | null>(null);
  const [rosterRevalidating, setRosterRevalidating] = useState(false);
  const [quotaBanner, setQuotaBanner] = useState<{
    reason: "kill_switch" | "cooldown" | "loop" | "hard_lock";
  } | null>(null);
  const [quotaCountdownSec, setQuotaCountdownSec] = useState(0);

  /** True if the current user is admin (from ID token claim or session). Must be declared before any useCallback/useEffect that references it. */
  const isAdmin = useMemo(
    () => firebaseAdminClaim === true || !!user?.isAdmin || user?.role === "admin",
    [firebaseAdminClaim, user?.isAdmin, user?.role]
  );
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[Admin Auth State]", {
      isAdmin,
      firebaseAdminClaim,
      role: user?.role ?? null,
      userIsAdminFlag: user?.isAdmin ?? null,
      email: user?.email ?? null,
      uid: firebaseUser?.uid ?? null,
    });
  }, [isAdmin, firebaseAdminClaim, user?.role, user?.isAdmin, user?.email]);

  /** Verified clients: Firestore query listener matches `listMine` / `userEmail` (lowercase). */
  const clientBookingsFirestoreRealtime = useMemo(
    () =>
      useDatabase === true &&
      !!firebaseUser?.emailVerified &&
      !isAdmin &&
      !!user?.email &&
      user?.role !== "employee" &&
      user?.role !== "admin",
    [useDatabase, firebaseUser?.emailVerified, isAdmin, user?.email, user?.role],
  );

  const sessionUsesPortal = useMemo(
    () =>
      Boolean(
        user &&
          (user.role === "admin" || user.role === "employee" || user.isAdmin === true),
      ),
    [user],
  );

  useEffect(() => {
    if (useDatabase !== true) return;
    if (!sessionUsesPortal && firebaseAdminClaim !== true) return;
    schedulePortalChunkPrefetch();
  }, [useDatabase, sessionUsesPortal, firebaseAdminClaim]);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ reason?: "kill_switch" | "cooldown" | "loop" | "hard_lock" }>;
      const r = ce.detail?.reason;
      if (r === "kill_switch" || r === "cooldown" || r === "loop" || r === "hard_lock") {
        setQuotaBanner({ reason: r });
      }
    };
    window.addEventListener("lorena:api-quota", handler as EventListener);
    return () => window.removeEventListener("lorena:api-quota", handler as EventListener);
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setQuotaBanner(null);
      return;
    }
    const sync = () => {
      const now = Date.now();
      if (isSessionApiHardLocked()) {
        setQuotaBanner({ reason: "hard_lock" });
        return;
      }
      const { killSwitchUntil, cooldownUntil } = getQuotaBlockStatus();
      setQuotaBanner((prev) => {
        if (prev?.reason === "loop") return prev;
        if (now < killSwitchUntil || now < cooldownUntil) {
          return { reason: now < killSwitchUntil ? "kill_switch" : "cooldown" };
        }
        return null;
      });
    };
    sync();
    const id = window.setInterval(sync, 10_000);
    const onVis = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isAdmin]);

  useEffect(() => {
    if (quotaBanner?.reason !== "loop") return;
    const t = window.setTimeout(() => {
      setQuotaBanner((p) => (p?.reason === "loop" ? null : p));
    }, 12_000);
    return () => window.clearTimeout(t);
  }, [quotaBanner?.reason]);

  useEffect(() => {
    if (!isAdmin || !quotaBanner || quotaBanner.reason === "loop" || quotaBanner.reason === "hard_lock") {
      setQuotaCountdownSec(0);
      return;
    }
    const tick = () => {
      const { killSwitchUntil, cooldownUntil } = getQuotaBlockStatus();
      setQuotaCountdownSec(
        Math.max(0, Math.ceil((Math.max(killSwitchUntil, cooldownUntil) - Date.now()) / 1000)),
      );
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [isAdmin, quotaBanner]);

  // useCallback so the reference is stable across renders.
  // Without this, every component that lists showToast as a useEffect dep (VerifyEmailPending, etc.)
  // re-fires its effect on every App render, causing "Maximum update depth exceeded" when
  // emailVerified flips to true and the effect calls both showToast and navigate.
  // Defined before any useEffect that calls showToast (e.g. booking_requests listener).
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const legacy = localStorage.getItem(LORENA_API_ACCESS_TOKEN_KEY);
        if (legacy && !sessionStorage.getItem(LORENA_API_ACCESS_TOKEN_KEY)) {
          sessionStorage.setItem(LORENA_API_ACCESS_TOKEN_KEY, legacy);
          localStorage.removeItem(LORENA_API_ACCESS_TOKEN_KEY);
        }
      } catch {
        /* ignore */
      }
    }
    setApiBearerResolver(async () => {
      const auth = getFirebaseAuth();
      const u = auth?.currentUser;
      if (u) {
        try {
          return await u.getIdToken();
        } catch {
          return null;
        }
      }
      if (typeof window !== "undefined") {
        return sessionStorage.getItem(LORENA_API_ACCESS_TOKEN_KEY);
      }
      return null;
    });
  }, []);

  const dismissToast = useCallback(() => {
    if (toastHideTimerRef.current) {
      clearTimeout(toastHideTimerRef.current);
      toastHideTimerRef.current = null;
    }
    if (toastUndoClearRef.current) {
      clearTimeout(toastUndoClearRef.current);
      toastUndoClearRef.current = null;
    }
    setToast((prev) => ({ ...prev, isVisible: false, undo: undefined, undoExpiresAt: undefined }));
  }, []);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success', opts?: ShowToastOptions) => {
    // Suppress all error toasts on the verification page so users don't see permission/database errors while waiting to verify.
    const onVerifyRoute = typeof window !== "undefined" && (
      window.location.hash === "#/verify-email" ||
      window.location.hash.startsWith("#/verify-email/")
    );
    if (onVerifyRoute && type === "error") return;
    const hasUndo = !!opts?.undo?.onUndo;
    const duration = hasUndo ? 5000 : 3000;
    const undoExpiresAt = hasUndo ? Date.now() + 5000 : undefined;

    if (toastHideTimerRef.current) {
      clearTimeout(toastHideTimerRef.current);
      toastHideTimerRef.current = null;
    }
    if (toastUndoClearRef.current) {
      clearTimeout(toastUndoClearRef.current);
      toastUndoClearRef.current = null;
    }

    setToast({
      message,
      isVisible: true,
      type,
      undo: hasUndo ? opts!.undo! : undefined,
      undoExpiresAt,
    });

    toastHideTimerRef.current = setTimeout(() => {
      toastHideTimerRef.current = null;
      setToast((prev) => ({ ...prev, isVisible: false, undo: undefined, undoExpiresAt: undefined }));
    }, duration);

    if (hasUndo) {
      toastUndoClearRef.current = setTimeout(() => {
        toastUndoClearRef.current = null;
        setToast((prev) => ({ ...prev, undo: undefined, undoExpiresAt: undefined }));
      }, 5000);
    }
  }, []);

  // Circuit breaker: max 5 calls to refresh functions per 10 seconds to prevent read spikes from loops.
  const refreshGlobalBookingsTimestamps = useRef<number[]>([]);
  const refreshAllDataTimestamps = useRef<number[]>([]);
  /** Tight burst detector for refreshAllData (complements 10s / 5-call circuit breaker). See App.tsx sync + api.ts quota layer. */
  const refreshAllDataLoopTimestamps = useRef<number[]>([]);
  const adminDashboardLastRefreshedAt = useRef(0);
  /** SWR: last successful admin calendar window fetch (`listAdminWindow`). */
  const lastAdminBookingsFetchedAt = useRef(0);
  /** SWR: last successful team+clients roster fetch. */
  const lastAdminRosterFetchedAt = useRef(0);
  const CIRCUIT_BREAKER_WINDOW_MS = 10_000;
  const CIRCUIT_BREAKER_MAX_CALLS = 5;
  const REFRESH_LOOP_WINDOW_MS = 5_000;
  const refreshAllDataInFlightRef = useRef<Promise<void> | null>(null);
  /** Coalesce concurrent `ensureAdminBookingsFresh` calls (Portal bootstrap + `refreshAllData` + auth). */
  const ensureAdminBookingsInFlightRef = useRef<Promise<void> | null>(null);
  /** Last auth snapshot for admin calendar bump — avoids `listAdminWindow` on every Firebase token refresh. */
  const authLastSnapForAdminRefreshRef = useRef<{ uid: string | null; emailVerified: boolean | null }>({
    uid: null,
    emailVerified: null,
  });

  /** Avoid `listAdminWindow` until Bearer or shared secret is available (prevents empty cache / 401 on cold start). */
  const hasAdminWindowClientCredentialSync = useCallback((): boolean => {
    if (typeof window === "undefined") return true;
    if (getEffectiveAdminSecret()) return true;
    try {
      if (sessionStorage.getItem(LORENA_API_ACCESS_TOKEN_KEY)) return true;
    } catch {
      /* ignore */
    }
    const auth = getFirebaseAuth();
    return !!auth?.currentUser;
  }, []);

  const applyListAdminWindowToGlobalBookings = useCallback((bookingsInput: Booking[]) => {
    const normalized = bookingsInput.map((b) => normalizeBookingRowForAdminUi(b));
    const mergedFromApi = normalized.filter((b) => shouldIncludeNormalizedAdminBooking(b));
    setGlobalBookings((prev) => {
      const fromApiIds = new Set(mergedFromApi.map((x) => x.id));
      const kept = prev.filter((p) => !fromApiIds.has(p.id));
      return [...mergedFromApi, ...kept].sort((a, b) => (b.rawDate || "").localeCompare(a.rawDate || ""));
    });
  }, []);

  /** Admin Client Manager / calendar: realtime merge for `userEmail` (lowercase), same shape as listByUser. */
  const mergeGlobalBookingsFromAdminFirestore = useCallback((clientEmailLower: string, rows: Booking[]) => {
    const norm = (clientEmailLower || "").toLowerCase().trim();
    if (!norm) return;
    setGlobalBookings((prev) => {
      const others = prev.filter((b) => (b.userEmail || "").toLowerCase() !== norm);
      const byId = new Map<string, Booking>();
      others.forEach((b) => byId.set(b.id, b));
      for (const rawRow of rows) {
        const data = rawRow as unknown as Record<string, unknown>;
        if (isFirestoreBookingSoftDeleted(data)) continue;
        const b = normalizeBookingRowForAdminUi({ ...rawRow } as Booking);
        if ((b.userEmail || "").toLowerCase() !== norm) continue;
        if (!shouldIncludeNormalizedAdminBooking(b)) continue;
        byId.set(b.id, b);
      }
      return Array.from(byId.values()).sort((a, bk) => (bk.rawDate || "").localeCompare(a.rawDate || ""));
    });
  }, []);

  /**
   * Full admin dashboard sync (parallel Netlify calls; roster = team + clients, not full listAll).
   * Triggers: (1) once when admin session + DB become ready, (2) LorenasPad header Refresh.
   * Use Refresh users for listAll when you need legacy rows without `role: "client"`.
   * Inquiry convert uses addBooking + refreshInquiries instead (see Portal).
   */
  const refreshAllData = useCallback(
    async (opts?: RefreshAdminDashboardOptions) => {
      if (!useDatabase || !isAdmin) return;

      const loopNow = Date.now();
      refreshAllDataLoopTimestamps.current = refreshAllDataLoopTimestamps.current.filter(
        (t) => t > loopNow - REFRESH_LOOP_WINDOW_MS,
      );
      refreshAllDataLoopTimestamps.current.push(loopNow);
      if (refreshAllDataLoopTimestamps.current.length > 3) {
        console.warn(
          "[POTENTIAL_LOOP_DETECTED] refreshAllData invoked",
          refreshAllDataLoopTimestamps.current.length,
          "times in 5s",
        );
        window.dispatchEvent(
          new CustomEvent("lorena:api-quota", { detail: { reason: "loop" as const } }),
        );
        return;
      }

      const manual = opts?.manual === true;
      const skipFocusGuard = opts?.skipFocusGuard === true;

      if (!skipFocusGuard && shouldDeferAdminFetch()) {
        if (manual && typeof document !== "undefined" && document.visibilityState === "hidden") {
          showToast("Switch to this tab to refresh.", "info");
        }
        return;
      }

      // Manual Refresh always refetches (no 30s debounce) so new clients and bookings appear immediately.

      // Circuit breaker to prevent API call spikes
      const now = Date.now();
      const windowStart = now - CIRCUIT_BREAKER_WINDOW_MS;
      const recent = (refreshAllDataTimestamps.current = refreshAllDataTimestamps.current.filter(
        (t) => t > windowStart,
      ));
      if (recent.length >= CIRCUIT_BREAKER_MAX_CALLS) {
        console.error(
          "[refreshAllData] Circuit breaker: too many calls in 10s (" + recent.length + "). Skipping API call to prevent read spike.",
        );
        return;
      }
      refreshAllDataTimestamps.current.push(now);

      const inFlight = refreshAllDataInFlightRef.current;
      if (inFlight) return inFlight;

      const run = async () => {
        if (manual) {
          invalidateAdminDashboardCache();
        } else {
          await sleep(randomJitterMs());
        }
        setRosterRevalidating(true);
        try {
          const [bookingsResult, bookingRequestsResult, teamResult, clientsResult, inquiriesResult, blockedDaysResult] =
            await Promise.allSettled([
              api.bookings.listAdminWindow({ forceRefresh: manual }),
              api.bookings.listBookingRequests(),
              api.users.listTeam(),
              api.users.listClients(),
              api.inquiries.list(),
              api.blockedDays.get(),
            ]);

          unstable_batchedUpdates(() => {
            if (bookingsResult.status === "fulfilled") {
              const rawListAdminWindow = bookingsResult.value;
              const { bookings: allBookings } = rawListAdminWindow;
              const bookings = (allBookings || []) as Booking[];
              if (bookings.length === 0) {
                let serialized = "";
                try {
                  serialized = JSON.stringify(rawListAdminWindow, null, 2);
                } catch {
                  serialized = "(JSON.stringify failed — see object below)";
                }
                console.error(
                  "[refreshAllData] listAdminWindow returned ZERO bookings — raw API value (object + JSON):",
                  "\n--- object ---\n",
                  rawListAdminWindow,
                  "\n--- JSON ---\n",
                  serialized,
                );
              }
              if (import.meta.env.DEV) {
                console.log("API RESPONSE RECEIVED:", bookings);
              }
              applyListAdminWindowToGlobalBookings(bookings);
              lastAdminBookingsFetchedAt.current = Date.now();
            } else {
              console.error("[Admin] Failed to refresh bookings:", bookingsResult.reason);
            }

            if (bookingsResult.status === "fulfilled" || bookingRequestsResult.status === "fulfilled") {
              const legacyInbox =
                bookingsResult.status === "fulfilled"
                  ? ((bookingsResult.value.bookings || []) as Booking[]).filter((b) => b.status === "new_request")
                  : [];
              const colInbox =
                bookingRequestsResult.status === "fulfilled"
                  ? ((bookingRequestsResult.value.requests || []) as Booking[])
                  : [];
              const byId = new Map<string, Booking>();
              for (const r of legacyInbox) byId.set(r.id, r);
              for (const r of colInbox) byId.set(r.id, r);
              setBookingRequests(
                Array.from(byId.values()).sort((a, b) => {
                  const ta = (a as unknown as { createdAt?: { seconds?: number } | string }).createdAt;
                  const tb = (b as unknown as { createdAt?: { seconds?: number } | string }).createdAt;
                  const sa =
                    typeof ta === "object" && ta !== null ? ((ta as { seconds?: number }).seconds ?? 0) : 0;
                  const sb =
                    typeof tb === "object" && tb !== null ? ((tb as { seconds?: number }).seconds ?? 0) : 0;
                  return sb - sa;
                }),
              );
            }
            if (bookingRequestsResult.status === "rejected") {
              console.error("[Admin] Failed to refresh booking_requests:", bookingRequestsResult.reason);
            }

            if (teamResult.status === "rejected") {
              console.error("[Admin] Failed to refresh team roster:", teamResult.reason);
            }
            if (clientsResult.status === "rejected") {
              console.error("[Admin] Failed to refresh clients roster:", clientsResult.reason);
            }
            setRegisteredUsers((prev) => {
              const merged = applyDashboardRosterFromTeamAndClients(prev, teamResult, clientsResult);
              return merged;
            });

            if (inquiriesResult.status === "fulfilled") {
              const { inquiries: allInquiries } = inquiriesResult.value;
              setInquiries((allInquiries || []) as Inquiry[]);
            } else {
              console.error("[Admin] Failed to refresh inquiries:", inquiriesResult.reason);
            }

            if (blockedDaysResult.status === "fulfilled") {
              const { blockedDays: days } = blockedDaysResult.value;
              setBlockedDays(days || []);
            } else {
              console.error("[Admin] Failed to refresh blocked days:", blockedDaysResult.reason);
            }

            if (teamResult.status === "fulfilled" && clientsResult.status === "fulfilled") {
              lastAdminRosterFetchedAt.current = Date.now();
            }
          });

          adminDashboardLastRefreshedAt.current = Date.now();
        } catch (error) {
          const { handleError } = await import("./utils/errorHandler");
          const errorMessage = handleError(error, "refreshAllData", "Failed to refresh admin data");
          showToast(`Failed to refresh data: ${errorMessage}`, "error");
        } finally {
          setRosterRevalidating(false);
        }
      };

      const p = run().finally(() => {
        if (refreshAllDataInFlightRef.current === p) refreshAllDataInFlightRef.current = null;
      });
      refreshAllDataInFlightRef.current = p;
      return p;
    },
    [useDatabase, isAdmin, showToast, applyListAdminWindowToGlobalBookings],
  );

  /** Lighter roster sync: team + clients only; prior roster kept if either call fails (applyDashboardRosterFromTeamAndClients). */
  const refreshRosterOnly = useCallback(
    async (rosterOpts?: { skipFocusGuard?: boolean }) => {
      if (!useDatabase || !isAdmin) return;
      if (!rosterOpts?.skipFocusGuard && shouldDeferAdminFetch()) {
        return;
      }
      setRosterRevalidating(true);
      try {
        const [teamResult, clientsResult] = await Promise.allSettled([
          api.users.listTeam(),
          api.users.listClients(),
        ]);
        unstable_batchedUpdates(() => {
          setRegisteredUsers((prev) => applyDashboardRosterFromTeamAndClients(prev, teamResult, clientsResult));
        });
        if (teamResult.status === "fulfilled" && clientsResult.status === "fulfilled") {
          lastAdminRosterFetchedAt.current = Date.now();
        }
      } catch (e) {
        console.warn("[refreshRosterOnly]", getErrorMessage(e));
      } finally {
        setRosterRevalidating(false);
      }
    },
    [useDatabase, isAdmin],
  );

  const ensureAdminBookingsFresh = useCallback(
    async (ttlMs: number) => {
      // Admin calendar dots must not depend on Firestore availability.
      // This is a Netlify API call (`listAdminWindow`) and should run as soon as the admin session is active.
      if (!isAdmin) {
        if (import.meta.env.DEV) {
          console.warn("[ensureAdminBookingsFresh] skipped: isAdmin=false", {
            firebaseAdminClaim,
            role: user?.role ?? null,
            userIsAdminFlag: user?.isAdmin ?? null,
          });
        }
        return;
      }
      if (ttlMs > 0 && Date.now() - lastAdminBookingsFetchedAt.current < ttlMs) return;
      if (!hasAdminWindowClientCredentialSync()) return;
      const inflight = ensureAdminBookingsInFlightRef.current;
      if (inflight) return inflight;
      const flightHolder = {} as { p: Promise<void> };
      flightHolder.p = (async () => {
        try {
          const { bookings: allBookings } = await api.bookings.listAdminWindow();
          if (import.meta.env.DEV) {
            console.log("API RESPONSE RECEIVED:", allBookings || []);
          }
          unstable_batchedUpdates(() => {
            applyListAdminWindowToGlobalBookings((allBookings || []) as Booking[]);
          });
          lastAdminBookingsFetchedAt.current = Date.now();
        } catch (e) {
          console.warn("[ensureAdminBookingsFresh]", getErrorMessage(e));
        } finally {
          if (ensureAdminBookingsInFlightRef.current === flightHolder.p) {
            ensureAdminBookingsInFlightRef.current = null;
          }
        }
      })();
      ensureAdminBookingsInFlightRef.current = flightHolder.p;
      return flightHolder.p;
    },
    [isAdmin, applyListAdminWindowToGlobalBookings, hasAdminWindowClientCredentialSync, firebaseAdminClaim, user?.role, user?.isAdmin],
  );

  const ensureAdminBookingsFreshRef = useRef(ensureAdminBookingsFresh);
  useEffect(() => {
    ensureAdminBookingsFreshRef.current = ensureAdminBookingsFresh;
  }, [ensureAdminBookingsFresh]);

  const isAdminRef = useRef(!!isAdmin);
  useEffect(() => {
    isAdminRef.current = !!isAdmin;
  }, [isAdmin]);

  /** Refetch the instant runtime admin secret or cross-tab storage updates (header becomes valid). */
  useEffect(() => {
    if (!isAdmin) return;
    const bump = () => {
      invalidateAdminDashboardCache();
      void ensureAdminBookingsFreshRef.current(0);
    };
    const onRuntimeSecret = () => bump();
    window.addEventListener(LORENA_RUNTIME_ADMIN_SECRET_EVENT, onRuntimeSecret);
    const onStorage = (e: StorageEvent) => {
      if (e.key === LORENA_RUNTIME_ADMIN_SECRET_KEY) bump();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(LORENA_RUNTIME_ADMIN_SECRET_EVENT, onRuntimeSecret);
      window.removeEventListener("storage", onStorage);
    };
  }, [isAdmin]);

  const wasAdminRef = useRef(false);
  useEffect(() => {
    if (!isAdmin) {
      wasAdminRef.current = false;
      return;
    }
    if (!wasAdminRef.current) {
      wasAdminRef.current = true;
      if (hasAdminWindowClientCredentialSync()) {
        invalidateAdminDashboardCache();
        void ensureAdminBookingsFresh(0);
      }
    }
  }, [isAdmin, hasAdminWindowClientCredentialSync, ensureAdminBookingsFresh]);

  const ensureAdminRosterFresh = useCallback(
    async (ttlMs: number) => {
      if (!useDatabase || !isAdmin) return;
      if (ttlMs > 0 && Date.now() - lastAdminRosterFetchedAt.current < ttlMs) return;
      await refreshRosterOnly({ skipFocusGuard: true });
    },
    [useDatabase, isAdmin, refreshRosterOnly],
  );

  /** No BC: one cheap calendar window pull (storage fallback). */
  const resyncAdminBookingsWindowOnly = useCallback(async () => {
    await ensureAdminBookingsFresh(0);
  }, [ensureAdminBookingsFresh]);

  /** Tab backgrounded 30m+: invalidate trust in SWR; pull calendar window on return. */
  const adminPortalLastHiddenAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAdmin || !useDatabase) return;
    const ZOMBIE_MS = 30 * 60 * 1000;
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        adminPortalLastHiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = adminPortalLastHiddenAtRef.current;
      if (hiddenAt != null && Date.now() - hiddenAt >= ZOMBIE_MS) {
        void resyncAdminBookingsWindowOnly();
      }
      adminPortalLastHiddenAtRef.current = null;
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [isAdmin, useDatabase, resyncAdminBookingsWindowOnly]);

  const peerVersionMismatchToastRef = useRef(false);

  useEffect(() => {
    if (!isAdmin) return;
    return subscribePortalPeerSync((msg) => {
      if (msg.tabId === getPortalTabId()) return;
      if (msg.buildVersion.length > 0 && msg.buildVersion !== APP_BUILD_VERSION) {
        if (!peerVersionMismatchToastRef.current) {
          peerVersionMismatchToastRef.current = true;
          showToast("New version available — please refresh this tab.", "info");
        }
        return;
      }
      unstable_batchedUpdates(() => {
        if (msg.kind === "updateResult") {
          setGlobalBookings((prev) =>
            mergeGlobalBookingsFromUpdateResponse(prev, msg.updateId, msg.res, msg.clientMerged ?? null),
          );
        } else if (msg.kind === "upsertMany") {
          setGlobalBookings((prev) => mergeUpsertManyIntoGlobalBookings(prev, msg.bookings));
        } else if (msg.kind === "remove") {
          setGlobalBookings((prev) => prev.filter((b) => b.id !== msg.id));
        } else if (msg.kind === "acceptRequest") {
          setBookingRequests((prev) => prev.filter((r) => r.id !== msg.requestId));
          setGlobalBookings((prev) => mergeUpsertManyIntoGlobalBookings(prev, [msg.booking]));
        }
      });
      showToast("Schedule updated from another tab.", "info");
    });
  }, [isAdmin, showToast]);

  const probeDbReachable = useCallback(async () => {
    await sleep(randomJitterMs());
    try {
      const ok = await isDatabaseAvailable();
      setDbReachable(ok);
      setLastDbCheckAt(Date.now());
    } catch {
      setDbReachable(false);
      setLastDbCheckAt(Date.now());
    }
  }, []);

  useEffect(() => {
    if (!useDatabase || !isAdmin) return;
    const onVis = () => {
      if (document.visibilityState === "visible") void probeDbReachable();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [useDatabase, isAdmin, probeDbReachable]);

  useEffect(() => {
    if (!useDatabase || !isAdmin) return;
    void probeDbReachable();
    const t = window.setInterval(() => void probeDbReachable(), 5 * 60 * 1000);
    return () => window.clearInterval(t);
  }, [useDatabase, isAdmin, probeDbReachable]);

  useEffect(() => {
    if (!isAdmin) {
      setStripeWebhookLastError(null);
      return;
    }
    const fs = getFirestoreDb();
    if (!fs) return;
    const ref = doc(fs, "system_metrics", "stripe_webhook_last_error");
    // Single doc listener for admin header health; cleanup on unmount prevents duplicate subscriptions.
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setStripeWebhookLastError(null);
          return;
        }
        const d = snap.data();
        setStripeWebhookLastError({
          at: typeof d.at === "string" ? d.at : "",
          eventType: typeof d.eventType === "string" ? d.eventType : undefined,
          message: typeof d.message === "string" ? d.message : undefined,
        });
      },
      () => setStripeWebhookLastError(null),
    );
    return () => unsub();
  }, [isAdmin]);

  const refreshAllDataRef = useRef(refreshAllData);
  refreshAllDataRef.current = refreshAllData;

  // Priority redirect: if user is signed in but not verified, redirect via React Router
  // (not window.location.hash — that bypasses the router and can cause double-navigation loops).
  // VerificationGuard handles the actual <Navigate> render; this effect is a belt-and-suspenders
  // guard only for cases where the guard hasn't mounted yet.
  useEffect(() => {
    if (firebaseUser && !firebaseUser.emailVerified) {
      if (typeof window !== "undefined" &&
          !window.location.hash.includes("/verify-email")) {
        window.location.replace(window.location.pathname + window.location.search + "#/verify-email");
      }
    }
  }, [firebaseUser?.uid, firebaseUser?.emailVerified]);

  // Resolve admin from Firebase ID token custom claim (hybrid: claim takes precedence; fallback to session isAdmin/role).
  useEffect(() => {
    if (!firebaseUser) {
      setFirebaseAdminClaim(null);
      return;
    }
    const auth = getFirebaseAuth();
    const currentUser = auth?.currentUser;
    if (!currentUser || currentUser.uid !== firebaseUser.uid) {
      setFirebaseAdminClaim(null);
      return;
    }
    currentUser.getIdTokenResult().then((result) => {
      setFirebaseAdminClaim(result.claims.admin === true);
    }).catch(() => {
      setFirebaseAdminClaim(false);
    });
  }, [firebaseUser?.uid, firebaseUser?.email]);

  // Stable primitives derived from the firebaseUser object so useCallbacks/useEffects
  // can list them as deps without re-triggering on every firebaseUser object identity change.
  const fbUid = firebaseUser?.uid ?? null;
  const fbEmailVerified = firebaseUser?.emailVerified ?? null;

  const refreshGlobalBookings = useCallback((forUserEmail?: string, role?: User["role"]) => {
    if (useDatabase) {
      if (isAdmin) {
        // Admin: full dashboard sync is manual only (Refresh in LorenasPad) to protect Firebase quota.
        return;
      }
      if (forUserEmail || role === "employee") {
        void (async () => {
          await sleep(randomJitterMs());
          const now = Date.now();
          const windowStart = now - CIRCUIT_BREAKER_WINDOW_MS;
          const recent = (refreshGlobalBookingsTimestamps.current =
            refreshGlobalBookingsTimestamps.current.filter((t) => t > windowStart));
          if (recent.length >= CIRCUIT_BREAKER_MAX_CALLS) {
            console.error(
              "[refreshGlobalBookings] Circuit breaker: too many calls in 10s (" + recent.length + "). Skipping API call to prevent read spike.",
            );
            return;
          }
          refreshGlobalBookingsTimestamps.current.push(now);

          const listPromise =
            role === "employee"
              ? api.bookings.listAssignedToMe()
              : api.bookings.listMine(forUserEmail!);
          try {
            const { bookings: b } = await listPromise;
            const mine = (b || []) as Booking[];
            const em = forUserEmail!.toLowerCase();
            setGlobalBookings((prev) =>
              role === "employee"
                ? mergeEmployeeAssignedFromServer(prev, em, mine)
                : mergeClientBookingsFromServer(prev, em, mine),
            );
          } catch (err) {
            if (isInsufficientPermissionsError(err) && fbEmailVerified === false) {
              window.location.hash = "#/verify-email";
              return;
            }
            console.error("[refreshGlobalBookings] Failed to load user bookings:", getErrorMessage(err));
          }
        })();
      }
    } else {
      const storedUsers = localStorage.getItem('lorena_users_db');
      if (storedUsers) {
        try {
          const parsedUsers = JSON.parse(storedUsers);
          let allBookings: Booking[] = [];
          (Object.values(parsedUsers) as User[]).forEach((u) => {
            const userKey = `lorena_bookings_${u.email?.toLowerCase()}`;
            const b = localStorage.getItem(userKey);
            if (b) {
              try {
                allBookings = [...allBookings, ...JSON.parse(b)];
              } catch (e) {
                logError(e, "App:refreshGlobalBookings:parsePerUserBookings");
              }
            }
          });
          setGlobalBookings(allBookings);
        } catch (e) {
          logError(e, "App:refreshGlobalBookings:parseStoredUsers");
        }
      }
    }
    // Deps: use primitive values only — avoid full object refs that recreate the callback on every user/firebaseUser state change.
  }, [useDatabase, isAdmin, fbEmailVerified]);

  useEffect(() => {
    if (!clientBookingsFirestoreRealtime || !user?.email) return;
    // Guard: do not attach without a verified Firebase identity.
    if (!firebaseUser?.uid || !firebaseUser?.email) return;
    const fs = getFirestoreDb();
    if (!fs) return;

    const emailLower = firebaseUser.email.toLowerCase().trim();
    if (!emailLower) return;
    const debugAll = typeof window !== "undefined" && localStorage.getItem("lorena_debug_client_listener_all") === "1";

    /** Match by userEmail — same field used by listByUser/listMine Netlify query so any admin-saved booking is found. */
    const clientBookingsQuery = debugAll
      ? query(
          collection(fs, "bookings"),
          orderBy("rawDate", "desc"),
          orderBy(documentId(), "desc"),
          limit(50),
        )
      : query(
          collection(fs, "bookings"),
          where("userEmail", "==", emailLower),
          orderBy("rawDate", "desc"),
          orderBy(documentId(), "desc"),
          limit(300),
        );

    setBookingsLoading(true);
    const unsub = onSnapshot(
      clientBookingsQuery,
      (snap) => {
        const rows: Booking[] = [];
        for (const d of snap.docs) {
          const data = d.data() as Record<string, unknown>;
          // Only hide soft-deleted / archived — do not require active === true.
          if (isFirestoreBookingSoftDeleted(data)) continue;
          rows.push({ id: d.id, ...data } as Booking);
        }
        unstable_batchedUpdates(() => {
          setGlobalBookings((prev) => mergeClientBookingsFromServer(prev, emailLower, rows));
          setBookingsLoading(false);
        });
      },
      (err) => {
        console.warn("[bookings listener]", getErrorMessage(err));
        setBookingsLoading(false);
        invalidateBookingsForUser(emailLower);
        void api.bookings
          .listByUser(emailLower)
          .then(({ bookings: b }) => {
            const mine = (b || []) as Booking[];
            setGlobalBookings((prev) => mergeClientBookingsFromServer(prev, emailLower, mine));
          })
          .catch((err) => {
            logError(err, "App:clientBookingsListener:fallbackListByUser");
          });
      },
    );

    return () => unsub();
  }, [clientBookingsFirestoreRealtime, user?.email, firebaseUser?.email, firebaseUser?.uid]);

  useEffect(() => {
    const storedSession = localStorage.getItem('lorena_current_user');
    if (storedSession) {
      try {
        setUser(JSON.parse(storedSession));
      } catch (e) {
        logError(e, "App:hydrateSession:parseCurrentUser");
      }
    }

    const loadFromLocalStorage = () => {
      const storedUsers = localStorage.getItem('lorena_users_db');
      let parsedUsers: Record<string, User> = {};
      if (storedUsers) {
        try {
          parsedUsers = JSON.parse(storedUsers);
        } catch (e) {
          logError(e, "App:loadFromLocalStorage:parseUsers");
        }
      }
      let hasUpdates = false;
      defaultTeams.forEach(team => {
        if (!parsedUsers[team.email]) { parsedUsers[team.email] = team; hasUpdates = true; }
      });
      if (hasUpdates) localStorage.setItem('lorena_users_db', JSON.stringify(parsedUsers));
      setRegisteredUsers(parsedUsers);

      const storedInquiries = localStorage.getItem('lorena_inquiries');
      if (storedInquiries) {
        try {
          setInquiries(JSON.parse(storedInquiries));
        } catch (e) {
          logError(e, "App:loadFromLocalStorage:parseInquiries");
        }
      }
      const storedBlocked = localStorage.getItem('lorena_blocked_days');
      if (storedBlocked) {
        try {
          setBlockedDays(JSON.parse(storedBlocked));
        } catch (e) {
          logError(e, "App:loadFromLocalStorage:parseBlockedDays");
        }
      }

      let allBookings: Booking[] = [];
      Object.values(parsedUsers).forEach((u: User) => {
        const b = localStorage.getItem(`lorena_bookings_${u.email?.toLowerCase()}`);
        if (b) {
          try {
            allBookings = [...allBookings, ...JSON.parse(b)];
          } catch (e) {
            logError(e, "App:loadFromLocalStorage:parsePerUserBookings");
          }
        }
      });
      // Never overwrite globalBookings if the Firestore listener already populated it.
      // Use the functional updater so we read current state at update-time, not closure-time.
      setGlobalBookings(prev => {
        if (prev.length > 0 && allBookings.length === 0) {
          return prev; // Keep what the listener loaded
        }
        if (allBookings.length > 0) {
          return allBookings;
        }
        return prev;
      });
    };

    loadFromLocalStorage();

    const runConfigHealthCheck = async (): Promise<{
      httpOk: boolean;
      bodyOk: boolean;
      configurationOk: boolean | null;
    }> => {
      const notReachable = { httpOk: false, bodyOk: false, configurationOk: null as boolean | null };
      try {
        const healthBase =
          typeof window !== "undefined" &&
          window.location.hostname === "localhost" &&
          window.location.port &&
          window.location.port !== "8888"
            ? "http://localhost:8888/.netlify/functions"
            : `${window.location.origin}/.netlify/functions`;

        const res = await fetch(`${healthBase}/health-check`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ping: true }),
        });

        const responseText = await res.text();
        let payload = {} as {
          ok?: boolean;
          version?: string;
          configurationOk?: boolean;
          report?: string;
          backendPresence?: Record<string, boolean>;
          adminSecretConfigured?: boolean;
          appIdMatch?: boolean;
        };
        try {
          if (responseText) payload = JSON.parse(responseText) as typeof payload;
        } catch {
          /* non-JSON body (e.g. proxy HTML error page) */
        }

        if (!res.ok) {
          if (res.status === 503) {
            console.warn("[Config Health Check] Non-OK HTTP from health-check (diagnostic):", {
              status: res.status,
              statusText: res.statusText,
              bodyLength: responseText.length,
              bodyPreview: responseText.slice(0, 2000),
              parsedPayload: payload,
            });
          } else {
            console.warn("[Config Health Check] HTTP error (diagnostic):", {
              status: res.status,
              bodyPreview: responseText.slice(0, 800),
            });
          }
          return notReachable;
        }

        if (payload.ok === true) {
          const configurationOk =
            typeof payload.configurationOk === "boolean" ? payload.configurationOk : null;
          if (payload.configurationOk === false) {
            console.warn(
              "[Config Health Check] Server reachable but backend env incomplete. Common causes: missing STRIPE_SECRET_KEY, ADMIN_SECRET, or Firebase Admin vars on Netlify. appIdMatch=false means FIRESTORE_ARTIFACTS_APP_ID (Netlify/server) is set and does not equal lorena-cleaning — not VITE_FIREBASE_APP_ID. Admin API auth uses Authorization + optional X-Admin-Secret from VITE_ADMIN_SECRET / lorena_runtime_admin_secret (see api.ts); health-check does not validate the browser header.",
              {
                version: payload.version,
                report: payload.report || "Configuration Mismatch",
                backendPresence: payload.backendPresence,
                adminSecretConfigured: payload.adminSecretConfigured,
                appIdMatch: payload.appIdMatch,
              },
            );
          }
          return { httpOk: true, bodyOk: true, configurationOk };
        }

        console.warn("[Config Health Check] Unexpected health-check body", {
          status: res.status,
          bodyPreview: responseText.slice(0, 800),
          payload,
        });
        return { httpOk: true, bodyOk: false, configurationOk: null };
      } catch (error) {
        console.warn("[Config Health Check] Failed to reach endpoint:", getErrorMessage(error));
        return notReachable;
      }
    };

    const init = async () => {
      try {
        const health = await runConfigHealthCheck();
        const dbOk = await isDatabaseAvailable();
        const isLocalPortal =
          typeof window !== "undefined" &&
          (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
        const healthSoftFail =
          !health.httpOk ||
          !health.bodyOk ||
          health.configurationOk === false;
        if (isLocalPortal && healthSoftFail) {
          console.warn(
            "[Config Health Check] Health probe did not report full success on localhost — keeping database mode enabled so you can test Netlify APIs. Restore strict gating for production.",
          );
          setRelaxPortalMutationLock(true);
          setUseDatabase(true);
        } else {
          setRelaxPortalMutationLock(false);
          setUseDatabase(dbOk);
        }

        if (dbOk) {
          try {
            // Determine if the stored session user is an admin to decide whether
            // to run expensive listAll queries on initial load.
            const stored = localStorage.getItem('lorena_current_user');
            let isBootstrapAdmin = false;
            let isBootstrapEmployee = false;
            if (stored) {
              try {
                const parsed = JSON.parse(stored) as Partial<User>;
                const role = parsed.role;
                const isAdminFlag = !!parsed.isAdmin;
                isBootstrapAdmin = !!(parsed && (isAdminFlag || role === 'admin'));
                isBootstrapEmployee = role === 'employee';
              } catch {
                isBootstrapAdmin = false;
                isBootstrapEmployee = false;
              }
            }

            // Admin: skip listAll / inquiries here — one `refreshAllData` in the admin effect loads
            // bookings, booking_requests, users, inquiries, and blocked days (avoids duplicate reads).
            const bookingsBootstrap =
              isBootstrapAdmin
                ? Promise.resolve({ bookings: [] })
                : isBootstrapEmployee
                  ? api.bookings.listAssignedToMe()
                  : Promise.resolve({ bookings: [] });

            const [blockedResult, bookingsResult] = await Promise.allSettled([
              api.blockedDays.get(),
              bookingsBootstrap,
            ]);
            if (blockedResult.status === "fulfilled") {
              setBlockedDays(blockedResult.value.blockedDays || []);
            } else {
              console.warn("[init] blockedDays.get failed:", (blockedResult as PromiseRejectedResult).reason);
            }
            if (bookingsResult.status === "fulfilled") {
              const bootstrapBookings = (bookingsResult.value.bookings || []) as Booking[];
              // Never overwrite globalBookings if the Firestore listener already populated it.
              // Use functional updater to read current state at update-time, not closure-time.
              setGlobalBookings(prev => {
                if (prev.length > 0 && bootstrapBookings.length === 0) {
                  return prev; // Keep what the listener loaded
                }
                if (bootstrapBookings.length > 0) {
                  return bootstrapBookings;
                }
                return prev;
              });
            } else {
              console.warn("[init] bookings.listAll failed (data not loaded; session unchanged):", (bookingsResult as PromiseRejectedResult).reason);
              // On bootstrap failure, never clear bookings that the listener already loaded.
              setGlobalBookings(prev => {
                if (prev.length > 0) return prev; // Keep listener data on bootstrap failure
                return [];
              });
            }
          } catch (e) {
            console.error('[DB init error]', e);
            if ((e as Error)?.message?.toLowerCase().includes("permission")) {
              console.error("[init] May have Missing Permissions – check Netlify function logs.");
            }
            setUseDatabase(false);
          }
        }
      } catch (e) {
        console.error("[init] Uncaught bootstrap error:", e);
        setRelaxPortalMutationLock(false);
        setUseDatabase(false);
      } finally {
        setUseDatabase((prev) => (prev === null ? false : prev));
      }
    };
    init();
  }, []);

  // Self-healing profile: ensure /artifacts/${appId}/users/${uid}/profile exists for authenticated Firebase users.
  const ensureArtifactsProfile = useCallback(async (fbUser: { uid: string; email: string | null; emailVerified?: boolean }): Promise<boolean> => {
    // Import the firebaseAccess utility
    const { directFirestoreOperation } = await import('./utils/firebaseAccess');
    const { COLLECTIONS, paths } = await import('./shared-config');
    
    // Guard: only run once per UID per page session.
    // Without this, every onAuthStateChanged or poll tick that fails (permission denied,
    // network error) would re-call this function, creating a Firestore write loop.
    if (_ensureProfileGuard.has(fbUser.uid)) return true;
    _ensureProfileGuard.add(fbUser.uid);

    try {
      // Use the approved direct operation pattern
      return await directFirestoreOperation('ensureArtifactsProfile', async () => {
        const firestoreDb = getFirestoreDb();
        if (!firestoreDb) return false;
        
        const appId = FIRESTORE_ARTIFACTS_APP_ID;
        const profileRef = doc(firestoreDb, COLLECTIONS.ARTIFACTS, appId, COLLECTIONS.USERS, fbUser.uid, COLLECTIONS.PROFILE, ARTIFACTS_PROFILE_DOC_ID);
        
        // Parent doc — never write undefined; Firestore throws "Unsupported field value: undefined".
        await setDoc(
          doc(firestoreDb, COLLECTIONS.ARTIFACTS, appId, COLLECTIONS.USERS, fbUser.uid),
          { createdAt: serverTimestamp(), role: "client", appId: FIRESTORE_ARTIFACTS_APP_ID },
          { merge: true }
        );
        
        const snap = await getDoc(profileRef);
        const profileExists = snap.exists();
        
        if (!profileExists) {
          // All fields must be non-undefined. Use null for missing optional strings.
          await setDoc(profileRef, {
            setupComplete: true,
            email: fbUser.email ?? "",
            emailVerified: fbUser.emailVerified === true,
            name: null,  // display name not available from the Auth object in this path
            createdAt: serverTimestamp(),
            role: "client",
            appId: FIRESTORE_ARTIFACTS_APP_ID,
          });
          return true;
        }
        
        // Existing profile: ensure setupComplete is set so legacy docs never block login.
        const data = snap.data() as { setupComplete?: boolean } | undefined;
        if (data?.setupComplete !== true) {
          await setDoc(profileRef, { setupComplete: true }, { merge: true });
        }
        
        return true;
      });
    } catch (e) {
      // Import error handler
      const { logError, ErrorType } = await import('./utils/errorHandler');
      
      // Log with structured error information
      const enhancedError = logError(
        e, 
        "ensureArtifactsProfile", 
        `Failed to ensure profile for uid ${fbUser.uid}`
      );
      
      // Handle specific error types
      if (enhancedError.type === ErrorType.PERMISSION) {
        console.warn(`[Auth] Permission denied when writing profile for uid ${fbUser.uid}. User may need to verify email first.`);
      } else if (enhancedError.type === ErrorType.QUOTA_EXCEEDED) {
        console.error(`[Auth] Firebase quota exceeded when writing profile for uid ${fbUser.uid}. Consider implementing rate limiting.`);
      }
      
      // Remove from guard on failure so it can be retried if auth state changes again
      // (e.g., after a successful token refresh). This is safe because we've already
      // added a delay via the guard — we won't loop on every render.
      _ensureProfileGuard.delete(fbUser.uid);
      return false;
    }
  }, []);

  // When Firebase Auth reports the user is signed out (token revoked, expired, etc.),
  // clear the app session for client users. Admin/team users bypass Firebase Auth
  // so we must not kick them out. When signed in, run self-healing and debug logs.
  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) return;
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (!fbUser) {
        authLastSnapForAdminRefreshRef.current = { uid: null, emailVerified: null };
        setFirebaseUser(null);
        const stored = localStorage.getItem('lorena_current_user');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            const role = parsed?.role;
            const isAdminSession = parsed?.isAdmin === true || role === "admin" || role === "employee";
            // Clear stale client sessions when Firebase has no active user. This prevents
            // old-project auth state from "sticking" after migration.
            if (!isAdminSession) {
              localStorage.removeItem('lorena_current_user');
              sessionStorage.clear();
              setUser(null);
            }
          } catch { /* corrupt data — clear it */ 
            localStorage.removeItem('lorena_current_user');
            setUser(null);
          }
        }
      } else {
        // Only update state when the actual values change — avoids creating a new object
        // reference on every onAuthStateChanged call, which would cause all consumers
        // that use firebaseUser as a dep to re-render unnecessarily.
        setFirebaseUser(prev => {
          const next = { uid: fbUser.uid, email: fbUser.email ?? null, emailVerified: fbUser.emailVerified };
          if (prev && prev.uid === next.uid && prev.email === next.email && prev.emailVerified === next.emailVerified) {
            return prev; // same values → same reference → no re-render
          }
          return next;
        });
        const snap = authLastSnapForAdminRefreshRef.current;
        const uidChanged = fbUser.uid !== snap.uid;
        const verifiedBecameTrue =
          snap.uid === fbUser.uid && snap.emailVerified === false && fbUser.emailVerified === true;
        authLastSnapForAdminRefreshRef.current = {
          uid: fbUser.uid,
          emailVerified: fbUser.emailVerified,
        };
        // Only bump admin window on sign-in / account switch / verify — not on silent token refresh (saves quota).
        if (isAdminRef.current && (uidChanged || verifiedBecameTrue)) {
          invalidateAdminDashboardCache();
          void ensureAdminBookingsFreshRef.current(0);
        }
        // Skip self-heal on the verification screen / when unverified to avoid Firestore reads that can trigger "insufficient authorization" toasts.
        if (fbUser.emailVerified) {
          ensureArtifactsProfile({ uid: fbUser.uid, email: fbUser.email ?? null, emailVerified: true }).catch((profileErr) => {
            console.warn("[auth] ensureArtifactsProfile failed:", getErrorMessage(profileErr));
          });
        }
      }
    });
    return () => unsubscribe();
  }, [ensureArtifactsProfile]);

  const userEmail = user?.email;
  useEffect(() => {
    if (useDatabase === null) return;
    if (firebaseUser && !firebaseUser.emailVerified) return;
    if (userEmail) {
      if (useDatabase) {
        if (clientBookingsFirestoreRealtime && getFirestoreDb()) {
          return;
        }
        setBookingsLoading(true);
        const listPromise =
          user?.role === "employee"
            ? api.bookings.listAssignedToMe()
            : api.bookings.listByUser(userEmail);
        const emailLower = userEmail.toLowerCase();
        listPromise
          .then(({ bookings: b }) => {
            const mine = (b || []) as Booking[];
            setGlobalBookings((prev) =>
              user?.role === "employee"
                ? mergeEmployeeAssignedFromServer(prev, emailLower, mine)
                : mergeClientBookingsFromServer(prev, emailLower, mine),
            );
          })
          .catch((err) => {
            if (isInsufficientPermissionsError(err) && firebaseUser && !firebaseUser.emailVerified) {
              window.location.hash = "#/verify-email";
            }
          })
          .finally(() => setBookingsLoading(false));
      } else {
        const storedKey = `lorena_bookings_${userEmail.toLowerCase()}`;
        const storedData = localStorage.getItem(storedKey);
        if (storedData) {
          try {
            const mine = JSON.parse(storedData) as Booking[];
            setGlobalBookings(prev => {
              const byId = new Map(prev.map(x => [x.id, x]));
              mine.forEach(x => byId.set(x.id, x));
              return Array.from(byId.values());
            });
          } catch (e) {
            logError(e, "App:loadUserBookings:parseStoredKey");
          }
        }
      }
    }
  }, [userEmail, user?.role, useDatabase, firebaseUser?.emailVerified, clientBookingsFirestoreRealtime]);

  // When client refocuses the tab, refetch (employees / non-Firestore clients). Realtime Firestore keeps verified clients in sync.
  useEffect(() => {
    if (!user?.email || useDatabase !== true) return;
    if (isAdmin) return;

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (clientBookingsFirestoreRealtime) return;
      invalidateBookingsForUser(user.email);
      refreshGlobalBookings(user.email, user.role);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [user?.email, user?.role, useDatabase, refreshGlobalBookings, isAdmin, clientBookingsFirestoreRealtime]);

  // ── ADMIN DATA: one initial API load when admin session + DB are ready (no polling) ───────────
  useEffect(() => {
    if (useDatabase !== true || !user?.email || !isAdmin) return;

    setBookingRequestsLoading(true);
    refreshAllDataRef.current({ skipFocusGuard: true }).finally(() => {
      setBookingRequestsLoading(false);
    });
  }, [user?.email, useDatabase, isAdmin]);

  // Further full sync: LorenasPad Refresh, or refreshInquiries after inquiry convert (Portal).

  const saveUserToDb = (u: User) => {
    if (!useDatabase) {
      setRegisteredUsers(prev => {
        const updated = { ...prev, [u.email.toLowerCase()]: u };
        localStorage.setItem('lorena_users_db', JSON.stringify(updated));
        return updated;
      });
    }
  };

  const createEmployee = async (name: string, email: string) => {
    if (useDatabase) {
      try {
        await api.users.createEmployee(name, email);
        await refreshUsers();
        showToast(`Employee ${name} created!`);
      } catch (e) {
        showToast((e as Error).message || 'Failed to create employee', 'error');
      }
    } else {
      const newUser: User = { name, email: email.toLowerCase(), role: 'employee', isAdmin: false };
      saveUserToDb(newUser);
      setRegisteredUsers(prev => ({ ...prev, [newUser.email]: newUser }));
      showToast(`Employee ${name} created!`);
    }
  };

  /** Production login: backend API only (api.auth.login for admin/team, Firebase for clients). No temp bypass. Clear storage on failure. */
  const login = async (email: string, password?: string) => {
    setAuthLoading(true);
    const normalizedEmail = (email || "").toLowerCase().trim();
    const inputPassword = (password != null && typeof password === "string") ? String(password).trim() : "";

    try {
      // Isolated try/catch so a 500 on blocked-days never blocks the login flow.
      // isDatabaseAvailable() already catches internally, but this outer guard
      // is an explicit, visible safety net per the "login bypass" requirement.
      let dbAvailable = false;
      try {
        dbAvailable = await isDatabaseAvailable();
      } catch {
        // Probe failed — treat as "no database" and continue in offline mode.
        dbAvailable = false;
      }
      setUseDatabase(dbAvailable);

      if (normalizedEmail === ADMIN_EMAIL) {
        if (!inputPassword) {
          setAuthLoading(false);
          throw new Error("Please enter Lorena's password.");
        }
        try {
          const loginRes = await api.auth.login(normalizedEmail, inputPassword);
          const profileUser = loginRes.user as User;
          if (loginRes.accessToken) {
            sessionStorage.setItem(LORENA_API_ACCESS_TOKEN_KEY, loginRes.accessToken);
          }
          setUser(profileUser);
          setUseDatabase(true);
          localStorage.setItem("lorena_current_user", JSON.stringify(profileUser));
          showToast("Welcome back, Lorena!");
          schedulePortalChunkPrefetch();
          // Also sign into Firebase Auth so the Firestore onSnapshot listener has
          // request.auth set. Best-effort: admin session works via Netlify even if
          // this fails (e.g. Firebase account uses a different password).
          const fbAuth = getFirebaseAuth();
          if (fbAuth && inputPassword) {
            signInWithEmailAndPassword(fbAuth, normalizedEmail, inputPassword).catch(() => {
              console.warn("[Admin] Firebase Auth sign-in skipped (account may use a different password). Firestore realtime listener will not start.");
            });
          }
        } catch (e) {
          localStorage.clear();
          sessionStorage.clear();
          setAuthLoading(false);
          throw new Error((e as Error)?.message === "Invalid password." ? "Invalid password." : "Invalid email or password.");
        }
        setAuthLoading(false);
        return;
      }

      const teamEmails = defaultTeams.map((t) => t.email.toLowerCase());
      const isTeamEmail = teamEmails.includes(normalizedEmail);

      if (isTeamEmail) {
        try {
          const loginRes = await api.auth.login(normalizedEmail, inputPassword);
          const profileUser = loginRes.user as User;
          if (loginRes.accessToken) {
            sessionStorage.setItem(LORENA_API_ACCESS_TOKEN_KEY, loginRes.accessToken);
          }
          setUser(profileUser);
          setUseDatabase(true);
          localStorage.setItem("lorena_current_user", JSON.stringify(profileUser));
          showToast(`Team Portal: ${profileUser.name}`);
          schedulePortalChunkPrefetch();
        } catch (e) {
          localStorage.clear();
          sessionStorage.clear();
          setAuthLoading(false);
          throw new Error("Invalid team password.");
        }
        setAuthLoading(false);
        return;
      }

      if (dbAvailable) {
        const auth = getFirebaseAuth();
        if (auth) {
          if (!inputPassword) {
            setAuthLoading(false);
            throw new Error("Please enter your password.");
          }
          let fbUser;
          try {
            const cred = await signInWithEmailAndPassword(auth, normalizedEmail, inputPassword);
            fbUser = cred.user;
          } catch (e: unknown) {
            const err = e as { code?: string; message?: string };
            localStorage.clear();
            sessionStorage.clear();
            setAuthLoading(false);
            let friendly = "Incorrect email or password.";
            if (err?.code === "auth/invalid-email") friendly = "Please enter a valid email address.";
            else if (err?.code === "auth/too-many-requests") friendly = "Too many attempts. Try again later.";
            else if (err?.code === "auth/user-not-found") friendly = "No account found with this email.";
            throw new Error(friendly);
          }
          await ensureArtifactsProfile({ uid: fbUser.uid, email: fbUser.email ?? null, emailVerified: fbUser.emailVerified });
          let profileUser: User | null = null;
          try {
            const { user: u } = await api.users.getByEmail(normalizedEmail);
            profileUser = u as User;
          } catch {
            try {
              const { user: u } = await api.auth.login(normalizedEmail);
              profileUser = u as User;
            } catch {
              const firestoreDb = getFirestoreDb();
              if (firestoreDb) {
                const appId = FIRESTORE_ARTIFACTS_APP_ID;
                const profileRef = doc(firestoreDb, "artifacts", appId, "users", fbUser.uid, "profile", ARTIFACTS_PROFILE_DOC_ID);
                const snap = await getDoc(profileRef);
                if (snap.exists()) {
                  const data = snap.data();
                  // Heal legacy profiles missing setupComplete so login never fails for verified clients with a real profile doc.
                  await setDoc(
                    profileRef,
                    { setupComplete: true, appId: FIRESTORE_ARTIFACTS_APP_ID },
                    { merge: true }
                  );
                  profileUser = {
                    name: (data?.name as string) || normalizedEmail.split("@")[0] || "Client",
                    email: (data?.email as string) || normalizedEmail,
                    phone: (data?.phone as string) || undefined,
                    role: (data?.role as User["role"]) || "client",
                    isAdmin: false,
                  };
                } else if (fbUser.emailVerified) {
                  const em = (normalizedEmail || (fbUser.email || "").toLowerCase()).trim();
                  if (em) {
                    await setDoc(
                      doc(firestoreDb, "artifacts", appId, "users", fbUser.uid),
                      { createdAt: serverTimestamp(), role: "client", appId: FIRESTORE_ARTIFACTS_APP_ID },
                      { merge: true }
                    );
                    await setDoc(profileRef, {
                      setupComplete: true,
                      email: em,
                      emailVerified: true,
                      name: fbUser.displayName || em.split("@")[0] || "Client",
                      createdAt: serverTimestamp(),
                      role: "client",
                      appId: FIRESTORE_ARTIFACTS_APP_ID,
                    }, { merge: true });
                    profileUser = {
                      name: fbUser.displayName || em.split("@")[0] || "Client",
                      email: em,
                      role: "client",
                      isAdmin: false,
                    };
                  }
                }
              }
              if (!profileUser) {
                setAuthLoading(false);
                throw new Error("Account not fully set up. Please contact support.");
              }
            }
          }
          setUser(profileUser);
          localStorage.setItem("lorena_current_user", JSON.stringify(profileUser));
          showToast(`Welcome back, ${profileUser?.name || "you"}!`);
        } else {
          const { user: u } = await api.auth.login(normalizedEmail, inputPassword);
          setUser(u as User);
          setUseDatabase(true);
          localStorage.setItem("lorena_current_user", JSON.stringify(u));
          showToast(`Welcome back, ${(u as User).name || "you"}!`);
        }
      } else {
        setAuthLoading(false);
        throw new Error("Service is temporarily unavailable. Please try again later.");
      }
    } catch (e) {
      if (e instanceof Error && e.message !== "Invalid password." && e.message !== "Invalid team password." && !e.message.startsWith("Incorrect") && !e.message.startsWith("Please enter") && !e.message.startsWith("No account") && !e.message.startsWith("Too many") && !e.message.startsWith("Account not")) {
        console.error("[login] Error:", e);
      }
      throw e;
    } finally {
      setAuthLoading(false);
    }
  };

  const signup = async (
    name: string,
    email: string,
    password?: string,
    phone?: string,
    address?: string
  ): Promise<void | { redirectToVerifyEmail: true }> => {
    setAuthLoading(true);
    await new Promise(resolve => setTimeout(resolve, 600));

    try {
      if (useDatabase) {
        const auth = getFirebaseAuth();
        if (!auth) {
          setAuthLoading(false);
          throw new Error("Sign-up is not configured. Please contact Lorena.");
        }
        const pwd = (password && password.length >= 6) ? password : "";
        if (!pwd) {
          setAuthLoading(false);
          throw new Error("Password must be at least 6 characters.");
        }
        const normalizedEmail = email.trim().toLowerCase();
        let uid: string;
        try {
          const userCred = await createUserWithEmailAndPassword(auth, normalizedEmail, pwd);
          uid = userCred.user.uid;
          try {
            const signupActionCodeSettings =
              typeof window !== "undefined"
                ? { url: "https://lorenacleaningllc.com/#/verify-email", handleCodeInApp: true }
                : undefined;
            await sendEmailVerification(userCred.user, signupActionCodeSettings);
          } catch { /* best-effort */ }
          /**
           * Firebase + Netlify signup: no client writes to `users` (or other roster collections) — those come from `api.auth.signup`.
           * Self-heal `artifacts/.../profile` only via `ensureArtifactsProfile` (same path as post-login).
           */
          await ensureArtifactsProfile({ uid, email: normalizedEmail, emailVerified: false });
          // Bust admin read caches immediately so the dashboard can show the new client row before Netlify finishes.
          invalidateAdminDashboardCache();
          const profileUser: User = {
            name,
            email: normalizedEmail,
            phone: phone || undefined,
            address: address || undefined,
            role: "client",
            uid,
          };
          setUser(profileUser);
          localStorage.setItem("lorena_current_user", JSON.stringify(profileUser));
          try {
            const res = await api.auth.signup(name, normalizedEmail, pwd, phone, address, uid);
            invalidateAdminDashboardCache();
            if (res.requiresVerification) {
              showToast("Check your email to verify your account.");
            }
          } catch (signupErr) {
            // Netlify function failed — self-healed artifacts profile may still exist; bust admin cache so Refresh sees the row.
            invalidateAdminDashboardCache();
            console.warn("[signup] API signup fallback failed after Firebase success:", getErrorMessage(signupErr));
          }
          setAuthLoading(false);
          return { redirectToVerifyEmail: true as const };
        } catch (e: unknown) {
          const err = e as { code?: string; message?: string };
          if (err?.code === "auth/email-already-in-use") {
            throw new Error("An account with this email already exists. Try signing in or use Forgot password.");
          }
          if (err?.code === "auth/invalid-email") {
            throw new Error("Please enter a valid email address.");
          }
          if (err?.code === "auth/weak-password") {
            throw new Error("Password should be at least 6 characters.");
          }
          throw new Error((err?.message as string) || "Could not create account. Please try again.");
        }
      } else {
        const normalizedEmail = email.toLowerCase();
        if (registeredUsers[normalizedEmail] ||
            Object.values(registeredUsers).some(u => (u.email || '').toLowerCase() === normalizedEmail)) {
          throw new Error("Account already exists.");
        }
        const newUser: User = { name, email: normalizedEmail, phone, address, role: 'client' };
        saveUserToDb(newUser);
        setRegisteredUsers(prev => ({ ...prev, [normalizedEmail]: newUser }));
        setUser(newUser);
        localStorage.setItem('lorena_current_user', JSON.stringify(newUser));
        showToast("Account created successfully!");
      }
    } catch (e) {
      throw e;
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    const auth = getFirebaseAuth();
    if (auth) {
      signOut(auth).catch((signOutErr) => {
        console.warn("[logout] Firebase signOut failed:", getErrorMessage(signOutErr));
      });
    }
    setFirebaseUser(null);
    invalidateApiCache();
    setUser(null);
    setGlobalBookings([]);
    localStorage.removeItem('lorena_current_user');
    sessionStorage.removeItem(LORENA_API_ACCESS_TOKEN_KEY);
    sessionStorage.clear();
    showToast("Signed out successfully.");
  };

  const handleResendEmail = useCallback(async () => {
    const auth = getFirebaseAuth();
    const currentUser = auth?.currentUser ?? null;
    if (!auth || !currentUser) {
      showToast("Your session may have expired. Please sign in again.", "error");
      return;
    }
    // Return URL: send user back to verify-email so they see the pending screen until they click the link.
    const actionCodeSettings = typeof window !== "undefined"
      ? { url: `${window.location.origin}#/verify-email`, handleCodeInApp: true }
      : undefined;
    try {
      await sendEmailVerification(currentUser, actionCodeSettings);
      setResendVerificationCooldownSeconds(60);
      showToast("Success");
    } catch (e: unknown) {
      const err = e as { code?: string; message?: string };
      if (err?.code === "auth/too-many-requests") {
        showToast("Too many verification emails sent. Please wait a few minutes before requesting another.", "error");
        return;
      }
      if (err?.code === "auth/user-token-expired" || err?.code === "auth/requires-recent-login") {
        showToast("Session expired. Please sign in again to resend the link.", "error");
        return;
      }
      showToast((err?.message as string) || "Failed to send. Try signing in again.", "error");
    }
  }, []);

  useEffect(() => {
    if (resendVerificationCooldownSeconds <= 0) return;
    const t = setInterval(() => {
      setResendVerificationCooldownSeconds((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendVerificationCooldownSeconds]);

  // Auto-poll verification status when user is logged in but email is not verified.
  // After reload(), force state update by spreading auth.currentUser so React sees the change.
  useEffect(() => {
    if (!firebaseUser || firebaseUser.emailVerified) return;
    const auth = getFirebaseAuth();
    if (!auth?.currentUser || auth.currentUser.uid !== firebaseUser.uid) return;

    const poll = async () => {
      const u = auth.currentUser;
      if (!u || u.uid !== firebaseUser.uid) return;
      try {
        await u.reload();
        const updated = auth.currentUser;
        if (!updated) return;
        // Bail out if nothing changed — the poll fires every 3 s and would otherwise
        // create a new firebaseUser object reference every tick, causing a full App re-render
        // that recreates showToast (not a useCallback) and re-fires every effect that lists it.
        setFirebaseUser(prev => {
          const next = { uid: updated.uid, email: updated.email ?? null, emailVerified: updated.emailVerified };
          if (prev && prev.uid === next.uid && prev.email === next.email && prev.emailVerified === next.emailVerified) {
            return prev;
          }
          return next;
        });
        if (updated.emailVerified) showToast("Email verified! You're all set.");
      } catch {
        // ignore reload errors (e.g. network)
      }
    };

    const intervalId = setInterval(poll, 3000);
    poll(); // run once immediately
    return () => clearInterval(intervalId);
  }, [firebaseUser?.uid, firebaseUser?.emailVerified]);

  const refreshVerificationStatus = useCallback(async () => {
    const auth = getFirebaseAuth();
    const u = auth?.currentUser;
    if (!u) return;
    try {
      await u.reload();
      const updated = auth.currentUser;
      if (!updated) return;
      setFirebaseUser(prev => {
        const next = { uid: updated.uid, email: updated.email ?? null, emailVerified: updated.emailVerified };
        if (prev && prev.uid === next.uid && prev.email === next.email && prev.emailVerified === next.emailVerified) {
          return prev;
        }
        return next;
      });
      if (updated.emailVerified) showToast("Email verified! You're all set.");
    } catch {
      showToast("Could not refresh. Try again in a moment.", "error");
    }
  }, [showToast]);

  const addInquiry = async (data: Omit<Inquiry, 'id' | 'timestamp' | 'status'>) => {
    if (useDatabase) {
      try {
        const auth = getFirebaseAuth();
        if (auth && !auth.currentUser) {
          await signInAnonymously(auth);
        }
        const res = (await api.inquiries.add(data.name, data.email, data.message)) as { inquiry?: Inquiry };
        if (res.inquiry) {
          setInquiries((prev) => [res.inquiry as Inquiry, ...prev]);
        }
      } catch (e) {
        showToast((e as Error).message || 'Failed to send', 'error');
      }
    } else {
      const newInquiry: Inquiry = { ...data, id: Math.random().toString(36).substr(2, 9), timestamp: new Date().toISOString(), status: 'New' };
      const updated = [newInquiry, ...inquiries];
      setInquiries(updated);
      localStorage.setItem('lorena_inquiries', JSON.stringify(updated));
    }
  };

  const toggleBlockDay = async (date: string) => {
    // Optimistic UI update
    const exists = blockedDays.includes(date);
    const newDays = exists ? blockedDays.filter(d => d !== date) : [...blockedDays, date];
    
    // Update state immediately for responsive UI
    setBlockedDays(newDays);
    
    if (useDatabase) {
      try {
        // API call to toggle the blocked day
        const { blockedDays: serverDays } = await api.blockedDays.toggle(date);
        
        // Update with server response
        setBlockedDays(serverDays || []);
      } catch (e) {
        // Rollback optimistic update on error
        setBlockedDays(blockedDays);
        
        showToast(`Failed to ${exists ? 'unblock' : 'block'} date: ${(e as Error).message || 'Unknown error'}`, 'error');
      }
    } else {
      // Local storage update
      localStorage.setItem('lorena_blocked_days', JSON.stringify(newDays));
    }
  };

  const isSlotAvailable = (_targetDateStr: string, _time24h: string, _excludeBookingId?: string) => true;

  const addDays = (dateStr: string, days: number): string => {
    const step = Number.isFinite(days) ? days : 0;
    const [y, m, d] = String(dateStr || "").split("-").map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return String(dateStr || "");
    const date = new Date(y, m - 1, d);
    if (Number.isNaN(date.getTime())) return String(dateStr || "");
    date.setDate(date.getDate() + step);
    const yy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  /** Calendar month step; clamps day to last day of target month (matches server `bookings.js`). */
  const addMonths = (dateStr: string, monthsToAdd: number): string => {
    const n = Number.isFinite(monthsToAdd) ? monthsToAdd : 0;
    const [y, m, d] = String(dateStr || "").split("-").map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return String(dateStr || "");
    const first = new Date(y, m - 1 + n, 1);
    if (Number.isNaN(first.getTime())) return String(dateStr || "");
    const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const day = Math.min(d, lastDay);
    first.setDate(day);
    const yy = first.getFullYear();
    const mm = String(first.getMonth() + 1).padStart(2, "0");
    const dd = String(first.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  const RECUR_WEEKLY_CAP = 104;
  const RECUR_MONTHLY_CAP = 24;

  const generateRecurringInstances = (baseBooking: Booking) => {
    const freq = String(baseBooking.frequency || "").trim();
    const anchorUid =
      baseBooking.uid != null && baseBooking.uid !== "" ? String(baseBooking.uid) : null;

    const pushRow = (instances: Booking[], i: number, currentDateStr: string) => {
      const dateParts = currentDateStr.split("-").map(Number);
      const uiDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
      const displayDate = Number.isNaN(uiDate.getTime())
        ? currentDateStr
        : uiDate.toLocaleDateString(undefined, {
            weekday: "long",
            month: "short",
            day: "numeric",
          });
      instances.push({
        ...baseBooking,
        id: i === 0 ? baseBooking.id : `${baseBooking.id}-rep-${i}`,
        rawDate: currentDateStr,
        date: displayDate,
        time: baseBooking.time,
        rawTime: baseBooking.rawTime,
        status: "Confirmed",
        ...(anchorUid ? { uid: anchorUid } : {}),
      });
    };

    const rowYear = (s: string) => {
      const y = parseInt(String(s || "").slice(0, 4), 10);
      return Number.isFinite(y) ? y : NaN;
    };

    if (freq === "Monthly") {
      const instances: Booking[] = [];
      let currentDateStr = baseBooking.rawDate;
      for (let i = 0; i < RECUR_MONTHLY_CAP; i++) {
        const cy = rowYear(currentDateStr);
        if (!Number.isFinite(cy) || cy > 2035) break;
        pushRow(instances, i, currentDateStr);
        currentDateStr = addMonths(currentDateStr, 1);
      }
      if (instances.length === 0) return [baseBooking];
      return instances;
    }

    if (freq !== "Weekly" && freq !== "Bi-weekly") return [baseBooking];
    const interval = freq === "Weekly" ? 7 : 14;
    const instances: Booking[] = [];
    let currentDateStr = baseBooking.rawDate;
    for (let i = 0; ; i++) {
      if (i >= RECUR_WEEKLY_CAP) break;
      const cy = rowYear(currentDateStr);
      if (!Number.isFinite(cy) || cy > 2035) break;
      pushRow(instances, i, currentDateStr);
      currentDateStr = addDays(currentDateStr, interval);
    }
    if (instances.length === 0) return [baseBooking];
    return instances;
  };

  const addBooking = async (
    booking: Booking,
    targetEmail?: string,
    targetUid?: string | null,
    options?: { singleOccurrence?: boolean },
  ) => {
    if (isInitializing) return;
    const emailToUse = (targetEmail || booking.userEmail || '').toLowerCase().trim();
    if (!emailToUse) {
      throw new Error("Missing client email");
    }
    if (!booking.address) {
      const allUsersArr = Object.values(registeredUsers);
      const clientUser = user?.email?.toLowerCase() === emailToUse ? user : allUsersArr.find((u: User) => u.email?.toLowerCase() === emailToUse);
      booking.address = (clientUser as User & { address?: string })?.address?.trim() || 'Address on file';
    }
    if (!booking.jobStatus) booking.jobStatus = 'Scheduled';

    // Roster may be keyed by UID (Firestore users) or email (in-memory users).
    const existsInRoster = registeredUsers[emailToUse] !== undefined ||
      Object.values(registeredUsers).some(u => (u.email || '').toLowerCase() === emailToUse);
    if (!existsInRoster) {
      const newUser: User = { name: booking.userName || 'New Client', email: emailToUse, role: 'client', phone: booking.userPhone };
      saveUserToDb(newUser);
      // Always update in-memory roster so the new client appears in the Clients tab immediately.
      // In database mode the backend creates the Firestore doc; this just keeps the UI in sync.
      setRegisteredUsers(prev => ({ ...prev, [emailToUse]: newUser }));
    }

    if (useDatabase) {
      try {
        // Never send a stale `uid` from a copied booking object; only stamp from explicit targetUid when provided.
        const rest = { ...booking } as Record<string, unknown>;
        delete rest.uid;
        const trimmedTargetUid =
          targetUid != null && String(targetUid).trim() ? String(targetUid).trim() : undefined;
        const b = {
          ...rest,
          userEmail: emailToUse,
          ...(trimmedTargetUid ? { uid: trimmedTargetUid } : {}),
        } as Booking;
        const addRes = (await api.bookings.add(b, emailToUse, trimmedTargetUid, {
          singleOccurrence: options?.singleOccurrence === true,
        })) as {
          bookings?: unknown[];
          successCount?: number;
          failCount?: number;
        };
        const addedList = (addRes.bookings || []) as Booking[];
        const failN = typeof addRes.failCount === "number" ? addRes.failCount : 0;
        const okN = typeof addRes.successCount === "number" ? addRes.successCount : addedList.length;
        if (failN > 0) {
          showToast(`Saved ${okN} of ${okN + failN} visits. Check Netlify logs for failed doc IDs.`, "info");
        }
        // Merge in one React commit so Admin Overview / calendar repaint together (instant feel).
        unstable_batchedUpdates(() => {
          setGlobalBookings((prev) => {
            const byId = new Map(prev.map((x) => [x.id, x]));
            addedList.forEach((x) => byId.set(x.id, x));
            return Array.from(byId.values()).sort((a, bk) => (bk.rawDate || "").localeCompare(a.rawDate || ""));
          });
          if (isAdmin && addedList.length > 0) {
            postPortalPeerMessage({ v: 1, kind: "upsertMany", bookings: addedList });
          }
        });
      } catch (e) {
        const { getUserFriendlyMessage } = await import("./utils/errorHandler");
        showToast(getUserFriendlyMessage(e) || "Failed to add booking", "error");
        throw e;
      }
    } else {
      const trimmedLocalUid =
        targetUid != null && String(targetUid).trim() ? String(targetUid).trim() : undefined;
      let finalBookingsToAdd = [booking];
      if (booking.status === "Confirmed" && options?.singleOccurrence !== true) {
        finalBookingsToAdd = generateRecurringInstances({
          ...booking,
          userEmail: emailToUse,
          ...(trimmedLocalUid ? { uid: trimmedLocalUid } : {}),
        });
      } else {
        finalBookingsToAdd = [{ ...booking, userEmail: emailToUse }];
      }
      const userKey = `lorena_bookings_${emailToUse}`;
      const stored = localStorage.getItem(userKey);
      let userBookings: Booking[] = [];
      if (stored) {
        try {
          userBookings = JSON.parse(stored);
        } catch (e) {
          logError(e, "App:addBookingLocal:parseUserBookings");
        }
      }
      const newBookings = [...finalBookingsToAdd, ...userBookings];
      localStorage.setItem(userKey, JSON.stringify(newBookings));
      refreshGlobalBookings();
    }
  };

  const updateBooking = async (id: string, updates: Partial<Booking>) => {
    if (isInitializing) return;
    const bookingToUpdate = globalBookings.find(b => b.id === id);
    const targetEmail = bookingToUpdate?.userEmail || '';

    if (useDatabase) {
      try {
        const res = (await api.bookings.update(id, updates as Record<string, unknown>)) as BookingsUpdateApiResult;
        const clientMerged = bookingToUpdate ? ({ ...bookingToUpdate, ...updates } as Booking) : null;
        setGlobalBookings((prev) => mergeGlobalBookingsFromUpdateResponse(prev, id, res, clientMerged));
        if (isAdmin) {
          postPortalPeerMessage({ v: 1, kind: "updateResult", updateId: id, res, clientMerged: clientMerged ?? undefined });
        }
      } catch (e) {
        showToast((e as Error).message || 'Failed to update', 'error');
        throw e;
      }
    } else if (targetEmail) {
      const userKey = `lorena_bookings_${targetEmail.toLowerCase()}`;
      const existingData = localStorage.getItem(userKey);
      if (existingData) {
        try {
          let userBookings: Booking[] = JSON.parse(existingData);
            if (updates.status === 'Confirmed' && bookingToUpdate?.status === 'Pending') {
            const master = { ...bookingToUpdate, ...updates };
            if (!master.address) master.address = (user?.address || (Object.values(registeredUsers).find((u: User) => u.email?.toLowerCase() === targetEmail.toLowerCase()) as User & { address?: string })?.address)?.trim() || 'Address on file';
            if (!master.jobStatus) master.jobStatus = 'Scheduled';
            const series = generateRecurringInstances(master);
            userBookings = userBookings.filter(b => b.id !== id);
            userBookings = [...series, ...userBookings];
          } else {
            userBookings = userBookings.map(b => b.id === id ? { ...b, ...updates } : b);
          }
          localStorage.setItem(userKey, JSON.stringify(userBookings));
          setGlobalBookings(prev => {
            const byId = new Map(prev.map(x => [x.id, x]));
            userBookings.forEach((b: Booking) => byId.set(b.id, b));
            return Array.from(byId.values()).sort((a, bk) => (bk.rawDate || '').localeCompare(a.rawDate || ''));
          });
        } catch (e) {
          logError(e, "App:updateBookingLocal:parseOrPersist");
        }
      }
    }
  };

  const updateBookingOptimistic = async (id: string, updates: Partial<Booking>) => {
    if (isInitializing) return;
    const bookingToUpdate = globalBookings.find((b) => b.id === id);
    if (!useDatabase) {
      await updateBooking(id, updates);
      return;
    }
    const snapshot = bookingToUpdate ? { ...bookingToUpdate } : null;
    if (snapshot) {
      setGlobalBookings((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)));
    }
    try {
      const res = (await api.bookings.update(id, updates as Record<string, unknown>)) as BookingsUpdateApiResult;
      const clientMerged = bookingToUpdate ? ({ ...bookingToUpdate, ...updates } as Booking) : null;
      setGlobalBookings((prev) => mergeGlobalBookingsFromUpdateResponse(prev, id, res, clientMerged));
      if (isAdmin) {
        postPortalPeerMessage({ v: 1, kind: "updateResult", updateId: id, res, clientMerged: clientMerged ?? undefined });
      }
    } catch (e) {
      if (snapshot) {
        setGlobalBookings((prev) => prev.map((b) => (b.id === id ? snapshot : b)));
      }
      throw e;
    }
  };

  const removeGlobalBookingsByIds = useCallback((ids: string[]) => {
    if (!ids.length) return;
    const idSet = new Set(ids);
    setGlobalBookings((prev) => prev.filter((b) => !idSet.has(b.id)));
    if (isAdmin) {
      for (const id of ids) {
        postPortalPeerMessage({ v: 1, kind: "remove", id });
      }
    }
  }, [isAdmin]);

  const deleteBooking = async (id: string) => {
    if (isInitializing) return;
    const bToDelete = globalBookings.find(b => b.id === id);
    const targetEmail = bToDelete?.userEmail || '';

    if (useDatabase) {
      try {
        await api.bookings.delete(id);
        setGlobalBookings(prev => prev.filter(b => b.id !== id));
        if (isAdmin) {
          postPortalPeerMessage({ v: 1, kind: "remove", id });
        }
        showToast("Visit archived (removed from schedule).", "success");
      } catch (e) {
        showToast((e as Error).message || "Failed to delete", "error");
      }
    } else if (targetEmail) {
      const userKey = `lorena_bookings_${targetEmail.toLowerCase()}`;
      const existingData = localStorage.getItem(userKey);
      if (existingData) {
        try {
          const userBookings: Booking[] = JSON.parse(existingData);
          const filtered = userBookings.filter(b => b.id !== id);
          localStorage.setItem(userKey, JSON.stringify(filtered));
          setGlobalBookings(prev => prev.filter(b => b.id !== id));
        } catch (e) {
          logError(e, "App:deleteBookingLocal:parseOrPersist");
        }
      }
    }
  };

  const deleteManyBookings = async (ids: string[]): Promise<boolean> => {
    if (isInitializing) {
      showToast("Still connecting — wait a moment and try again.", "info");
      return false;
    }
    if (ids.length === 0) return false;
    const idSet = new Set(ids.map((x) => String(x).trim()).filter(Boolean));
    const uniqueIds = Array.from(idSet);
    if (uniqueIds.length === 0) return false;

    // #region agent log
    agentDebugLog({
      hypothesisId: "H3",
      location: "App.tsx:deleteManyBookings",
      message: "entry",
      data: { idCount: uniqueIds.length, useDatabase: !!useDatabase, isInitializing },
    });
    // #endregion

    if (useDatabase) {
      const idSetForSnap = new Set(uniqueIds);
      const rollbackRows = globalBookings.filter((b) => idSetForSnap.has(b.id));
      // Optimistic UI + peer remove (admin): other Portal tabs drop rows immediately.
      removeGlobalBookingsByIds(uniqueIds);
      try {
        await Promise.all(uniqueIds.map((id) => api.bookings.delete(id)));
        const n = uniqueIds.length;
        showToast(`${n} visit${n === 1 ? "" : "s"} archived (removed from schedule).`, "success");
        // #region agent log
        agentDebugLog({
          hypothesisId: "H3",
          location: "App.tsx:deleteManyBookings",
          message: "api_success",
          data: { deletedCount: n },
        });
        // #endregion
        return true;
      } catch (e) {
        unstable_batchedUpdates(() => {
          setGlobalBookings((prev) => {
            const byId = new Map(prev.map((x) => [x.id, x]));
            for (const b of rollbackRows) {
              byId.set(b.id, b);
            }
            return Array.from(byId.values()).sort((a, bk) => (bk.rawDate || "").localeCompare(a.rawDate || ""));
          });
          if (isAdmin && rollbackRows.length > 0) {
            postPortalPeerMessage({ v: 1, kind: "upsertMany", bookings: rollbackRows });
          }
        });
        // #region agent log
        agentDebugLog({
          hypothesisId: "H3",
          location: "App.tsx:deleteManyBookings",
          message: "api_error",
          data: { err: String((e as Error)?.message ?? e).slice(0, 200) },
        });
        // #endregion
        showToast((e as Error).message || "Failed to delete visits", "error");
        return false;
      }
    }

    const byEmail = new Map<string, Set<string>>();
    for (const id of uniqueIds) {
      const row = globalBookings.find((b) => b.id === id);
      const em = (row?.userEmail || "").toLowerCase().trim();
      if (!em) continue;
      if (!byEmail.has(em)) byEmail.set(em, new Set());
      byEmail.get(em)!.add(id);
    }
    for (const [email, idSubset] of byEmail) {
      const userKey = `lorena_bookings_${email}`;
      const existingData = localStorage.getItem(userKey);
      if (!existingData) continue;
      try {
        const userBookings: Booking[] = JSON.parse(existingData);
        const remove = idSubset;
        localStorage.setItem(
          userKey,
          JSON.stringify(userBookings.filter((b) => !remove.has(b.id))),
        );
      } catch {
        /* ignore */
      }
    }
    removeGlobalBookingsByIds(uniqueIds);
    showToast(`${uniqueIds.length} visit${uniqueIds.length === 1 ? "" : "s"} removed (local).`, "success");
    return true;
  };

  const restoreBooking = useCallback(
    async (id: string) => {
      if (!useDatabase || !isAdmin) return;
      try {
        await api.bookings.restore(id);
        showToast("Booking restored.", "success");
        await refreshAllData({ skipFocusGuard: true });
      } catch (e) {
        showToast((e as Error).message || "Restore failed", "error");
        throw e;
      }
    },
    [useDatabase, isAdmin, refreshAllData, showToast],
  );

  const recordPayment = useCallback(async (email: string, amount: number, bookingId?: string, method = 'stripe') => {
    if (!useDatabase) return;
    try {
      await api.payments.record(email, amount, bookingId, method);
    } catch (e) {
      console.error('Failed to record payment:', e);
      throw e;
    }
  }, [useDatabase]);

  /** Build registeredUsers from API instead of direct Firestore access */
  const refreshUsers = useCallback(async () => {
    if (!useDatabase || !isAdmin) return;

    try {
      const { users: allUsers } = await api.users.listAll();
      const users = (allUsers || []) as User[];
      
      // Update registeredUsers with optimistic merging
      setRegisteredUsers(prev => mergeRosterOrKeepPrevious(prev, 
        users.reduce((acc, user) => {
          // Use UID as key if available, otherwise use email
          const key = user.uid || user.email.toLowerCase();
          acc[key] = user;
          return acc;
        }, {} as Record<string, User>)
      ));
    } catch (error) {
      console.error("[Admin] Error fetching users via API:", error);
      showToast("Failed to refresh users. Please try again.", "error");
    }
  }, [useDatabase, isAdmin, showToast]);

  const removeUser = useCallback((email: string) => {
    const emailNorm = (email || '').toLowerCase();
    setRegisteredUsers(prev => {
      const next = { ...prev };
      // Roster may be keyed by UID (Firestore users) or email (in-memory users).
      // Delete whichever key matches — check both the email key and any UID key whose user.email matches.
      delete next[emailNorm];
      const uidKey = Object.keys(prev).find(k => k !== emailNorm && (prev[k].email || '').toLowerCase() === emailNorm);
      if (uidKey) delete next[uidKey];
      try {
        localStorage.setItem('lorena_users_db', JSON.stringify(next));
      } catch (e) {
        logError(e, "App:removeUser:persistRoster");
      }
      return next;
    });
  }, []);

  const deleteEmployee = useCallback(async (emp: User) => {
    if (useDatabase) {
      await api.users.deleteUser(emp.email);
      await refreshUsers();
    } else {
      removeUser(emp.email);
    }
  }, [useDatabase, refreshUsers, removeUser]);

  const deleteClient = useCallback(async (client: User) => {
    const email = (client?.email || "").toLowerCase().trim();
    const uid = (client?.uid || "").trim();
    if (!email && !uid) return;
    
    // Immediate UI update: remove from state before API call completes
    // This provides a responsive user experience
    removeUser(email || `${uid}@profile-missing.lorena`);
    
    // Drop their bookings from calendar state when we know their booking email (not placeholder-only)
    if (email && !email.includes("profile-missing")) {
      setGlobalBookings((prev) =>
        prev.filter((b) => (b.userEmail || "").toLowerCase() !== email)
      );
    }
    
    if (useDatabase) {
      try {
        // API call to delete the client on the server
        await api.clients.delete(email, uid || undefined);
        showToast(`Client deleted successfully`, 'success');
      } catch (error) {
        console.error("[deleteClient] API call failed:", error);
        showToast(`Failed to delete client: ${(error as Error).message || 'Unknown error'}`, 'error');
      }
    } else {
      try {
        if (email && !email.includes("profile-missing")) {
          localStorage.removeItem(`lorena_bookings_${email}`);
        }
      } catch (e) {
        logError(e, "App:deleteClientLocal:removeBookingsKey");
      }
      refreshGlobalBookings();
    }
  }, [useDatabase, removeUser, refreshGlobalBookings, showToast]);

  // ── booking_requests: accept / dismiss ───────────────────────────────────
  const acceptBookingRequest = useCallback(async (requestId: string, booking: Booking, targetEmail: string) => {
    if (!useDatabase) return;

    // Snapshot before any setState so rollback never depends on stale closure timing.
    const requestSnapshot = bookingRequests.find(r => r.id === requestId);
    
    // Optimistic UI: add to calendar, remove from inbox immediately (rollback on failed accept).
    const optimisticBooking: Booking & { _optimistic: boolean } = {
      ...booking,
      id: booking.id || `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      status: 'Confirmed' as "Pending" | "Completed" | "Confirmed" | "new_request",
      _optimistic: true // Flag to identify optimistic updates
    };
    
    setGlobalBookings(prev => {
      const byId = new Map(prev.map(x => [x.id, x]));
      byId.set(optimisticBooking.id, optimisticBooking);
      return Array.from(byId.values()).sort((a, b) => (b.rawDate || '').localeCompare(a.rawDate || ''));
    });
    
    // Remove from requests immediately
    setBookingRequests(prev => prev.filter(r => r.id !== requestId));
    
    try {
      // API call to accept the booking request
      const { booking: accepted } = await api.requests.accept(
        requestId,
        booking as unknown as Record<string, unknown>,
        targetEmail,
        { retries: 2, retryDelay: 1000 } // Increased retries
      );
      
      // Update with the actual booking from the server
      setGlobalBookings(prev => {
        const byId = new Map(prev.map(x => [x.id, x]));
        // Remove optimistic booking if it exists
        if (optimisticBooking._optimistic) {
          byId.delete(optimisticBooking.id);
        }
        // Add the real booking from the server
        byId.set((accepted as Booking).id, accepted as Booking);
        return Array.from(byId.values()).sort((a, b) => (b.rawDate || '').localeCompare(a.rawDate || ''));
      });

      postPortalPeerMessage({
        v: 1,
        kind: "acceptRequest",
        requestId,
        booking: accepted,
      });

      showToast("Booking accepted and scheduled successfully!", "success");
    } catch (err) {
      console.error("[acceptBookingRequest] Failed:", err);
      const error = err instanceof Error ? err : new Error(String(err));
      const maybeErr = err as { code?: string; status?: number };
      
      // Rollback optimistic calendar row
      setGlobalBookings(prev => prev.filter(b => b.id !== optimisticBooking.id));

      const code = maybeErr.code;
      const isRequestConsumed = code === 'REQUEST_NOT_FOUND';

      // Restore inbox when accept did not succeed, unless the server indicates the request is already gone.
      if (!isRequestConsumed && requestSnapshot) {
        setBookingRequests(prev => (prev.some(r => r.id === requestId) ? prev : [...prev, requestSnapshot]));
      }

      const isNetworkError = code === 'TIMEOUT' ||
                            (maybeErr.status ?? 0) >= 500 ||
                            error.message.includes('RST_STREAM') ||
                            error.message.includes('INTERNAL');

      if (isNetworkError) {
        showToast(
          "Network issue while accepting booking. Please try again in a moment.",
          "error"
        );
      } else if (isRequestConsumed) {
        showToast(
          "This request has already been processed or deleted.",
          "info"
        );
      } else {
        showToast(
          "Error accepting booking: " + (error.message || "Unknown error"),
          "error"
        );
      }
    }
  }, [useDatabase, showToast, bookingRequests]);

  const deleteBookingRequest = useCallback(async (requestId: string) => {
    if (!useDatabase) return;
    
    // Store the original request for rollback if needed
    const originalRequest = bookingRequests.find(r => r.id === requestId);
    
    // Optimistic UI update: remove from requests immediately
    setBookingRequests(prev => prev.filter(r => r.id !== requestId));
    
    try {
      // API call to dismiss the booking request
      await api.requests.dismiss(requestId, { retries: 2, retryDelay: 1000 }); // Increased retries
      
      showToast("Request dismissed successfully", "success");
    } catch (err) {
      console.error("[deleteBookingRequest] Failed:", err);
      const error = err instanceof Error ? err : new Error(String(err));
      const maybeErr = err as { code?: string; status?: number };
      
      // Rollback optimistic update if it was a network error
      const isNetworkError = maybeErr.code === 'TIMEOUT' || 
                            (maybeErr.status ?? 0) >= 500 || 
                            error.message.includes('RST_STREAM') || 
                            error.message.includes('INTERNAL');
      
      if (isNetworkError) {
        // Add back to requests if we have the original
        if (originalRequest) {
          setBookingRequests(prev => [...prev, originalRequest]);
        }
        
        showToast(
          "Network issue while dismissing request. Please try again in a moment.", 
          "error"
        );
      } else if (maybeErr.code === 'REQUEST_NOT_FOUND') {
        showToast(
          "This request has already been processed or deleted.", 
          "info"
        );
        // Already removed from local state in optimistic update
      } else {
        if (originalRequest) {
          setBookingRequests(prev => (prev.some(r => r.id === requestId) ? prev : [...prev, originalRequest]));
        }
        showToast(
          "Error dismissing request: " + (error.message || "Unknown error"), 
          "error"
        );
      }
    }
  }, [useDatabase, showToast, bookingRequests]);

  /** Remove one id from the admin inbox (e.g. after API says request already processed). */
  const pruneBookingRequestFromInbox = useCallback((requestId: string) => {
    setBookingRequests((prev) => prev.filter((r) => r.id !== requestId));
  }, []);

  const updateClientUser = useCallback(async (email: string, updates: Partial<User>) => {
    const emailNorm = (email || '').toLowerCase();
    if (useDatabase) {
      await api.users.updateUser(email, {
        name: updates.name,
        phone: updates.phone,
        adminDisplayName: updates.adminDisplayName,
        address: updates.address,
        customRate: updates.customRate,
      });
      setRegisteredUsers(prev => {
        const key =
          prev[emailNorm] !== undefined
            ? emailNorm
            : Object.keys(prev).find(k => (prev[k].email || '').toLowerCase() === emailNorm);
        if (!key || !prev[key]) {
          void refreshUsers();
          return prev;
        }
        const current = prev[key];
        const nextUser: User = {
          ...current,
          ...(updates.name !== undefined ? { name: updates.name } : {}),
          ...(updates.phone !== undefined ? { phone: updates.phone } : {}),
          ...(updates.adminDisplayName !== undefined ? { adminDisplayName: updates.adminDisplayName } : {}),
          ...(updates.address !== undefined ? { address: updates.address } : {}),
          ...(updates.customRate !== undefined ? { customRate: updates.customRate } : {}),
        };
        return { ...prev, [key]: nextUser };
      });
    } else {
      setRegisteredUsers(prev => {
        // Roster may be keyed by UID or email — find whichever key holds this user.
        const key = prev[emailNorm] !== undefined
          ? emailNorm
          : Object.keys(prev).find(k => (prev[k].email || '').toLowerCase() === emailNorm) ?? emailNorm;
        const current = prev[key];
        if (!current) return prev;
        const next = { ...prev, [key]: { ...current, ...updates } };
        try {
          localStorage.setItem('lorena_users_db', JSON.stringify(next));
        } catch (e) {
          logError(e, "App:updateClientUserLocal:persistRoster");
        }
        return next;
      });
    }
  }, [useDatabase, refreshUsers]);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...updates };
      const key = (prev.email || '').toLowerCase();
      setRegisteredUsers(prevMap => ({ ...prevMap, [key]: next }));
      return next;
    });
  }, []);

  // Memoized so the allUsers array reference only changes when registeredUsers actually changes,
  // not on every App re-render (inline Object.values() would create a new array every render,
  // causing all Portal useMemos that depend on allUsers to recompute on every toast/loading toggle).
  const allUsers = useMemo(() => Object.values(registeredUsers), [registeredUsers]);

  /** Re-fetch inquiries only (after admin mutations). Cheaper than refreshAllData. */
  const refreshInquiries = useCallback(async () => {
    if (!useDatabase || !isAdmin) return;
    try {
      const { inquiries: next } = await api.inquiries.list();
      setInquiries((next || []) as Inquiry[]);
    } catch (e) {
      console.warn("[refreshInquiries] failed:", getErrorMessage(e));
    }
  }, [useDatabase, isAdmin]);

  // Per-user filtered view derived from the global source of truth.
  const bookings = useMemo(() => {
    if (!user?.email) return [];
    const email = user.email.toLowerCase().trim();
    return globalBookings.filter(
      (b) =>
        (b.userEmail || "").toLowerCase().trim() === email &&
        b.status !== "Deleted" &&
        b.active !== false,
    );
  }, [globalBookings, user?.email]);

  const activeBooking = useMemo(() => {
    if (bookings.length === 0) return null;
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const confirmed = bookings.filter(b => b.status === 'Confirmed').sort((a, b) => new Date(a.rawDate).getTime() - new Date(b.rawDate).getTime());
    return confirmed.find(b => parseLocalDate(b.rawDate).getTime() >= now.getTime()) || bookings.find(b => b.status === 'Pending') || null;
  }, [bookings]);

  return (
    <AuthContext.Provider value={{
      user, firebaseUser, isAdmin, login, signup, logout, bookingsLoading,
      handleResendEmail, refreshVerificationStatus, resendVerificationCooldownSeconds,
      sendPasswordResetEmail: async (email: string) => {
        const normalized = (email || "").toLowerCase().trim();
        const teamEmails = defaultTeams.map(t => t.email.toLowerCase());
        if (normalized === ADMIN_EMAIL || teamEmails.includes(normalized)) {
          const msg = "Lorena's and Team passwords are securely managed in the server vault and cannot be reset here.";
          showToast(msg, "error");
          throw new Error(msg);
        }

        const auth = getFirebaseAuth();
        if (!auth) {
          showToast("Password reset is not configured. Please contact Lorena to reset your password.", "error");
          throw new Error("Firebase Auth not configured");
        }
        const resetUrl = typeof window !== "undefined" ? `${window.location.origin}/#/reset-password` : "";
        try {
          await firebaseSendPasswordResetEmail(auth, email, { url: resetUrl });
          showToast("Check your email! We sent a link to reset your password.");
        } catch (e: unknown) {
          const err = e as { code?: string; message?: string };
          let friendly = "Failed to send reset email. Try again or contact Lorena.";
          if (err?.code === "auth/invalid-email") friendly = "Please enter a valid email address.";
          else if (err?.code === "auth/user-not-found") friendly = "No account found with that email.";
          else if (err?.code === "auth/too-many-requests") friendly = "Too many attempts. Please try again later.";
          throw new Error(friendly);
        }
      }, isLoading: authLoading,
      bookings, activeBooking, addBooking, updateBooking, updateBookingOptimistic, deleteBooking, deleteManyBookings, restoreBooking, updateUser,
      refreshGlobalBookings, refreshAllData, refreshRosterOnly, rosterRevalidating, refreshInquiries,
      ensureAdminBookingsFresh, ensureAdminRosterFresh, resyncAdminBookingsWindowOnly, useDatabase,
      dbReachable, relaxPortalMutationLock, lastDbCheckAt, stripeWebhookLastError,
      isSlotAvailable, inquiries, addInquiry,
      allUsers,
      globalBookings, bookingRequests, bookingRequestsLoading, acceptBookingRequest, deleteBookingRequest, pruneBookingRequestFromInbox, removeGlobalBookingsByIds, mergeGlobalBookingsFromAdminFirestore, blockedDays, toggleBlockDay, createEmployee, recordPayment, refreshUsers, updateClientUser, removeUser, deleteEmployee, deleteClient,
    }}>
      <ToastContext.Provider value={{ showToast, dismissToast }}>
        <PermissionErrorBoundary firebaseUser={firebaseUser}>
          <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <VerificationGuard>
            {isAdmin && quotaBanner ? (
              <div
                role="status"
                aria-live="polite"
                className="sticky top-0 border-b border-red-400/40 bg-red-600/92 px-4 py-2.5 text-center text-white shadow-[0_8px_30px_rgba(220,38,38,0.25)] backdrop-blur-md"
                style={{ zIndex: Z_INDEX.quotaBanner }}
              >
                <p className="text-sm font-bold tracking-tight">System on cooldown to prevent overload.</p>
                <p className="mt-0.5 text-xs font-medium text-red-50/95">
                  {quotaBanner.reason === "loop"
                    ? "Dashboard refresh was called too quickly; extra requests were skipped."
                    : quotaBanner.reason === "hard_lock"
                      ? "API calls are locked for this session after repeated quota trips. Refresh the page to reset."
                      : quotaCountdownSec > 0
                        ? `You can retry in about ${quotaCountdownSec}s.`
                        : "You can retry shortly."}
                </p>
              </div>
            ) : null}
            <ScrollToTop />
            <Layout toast={toast}>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<PageErrorBoundary pageName="Contact"><Contact /></PageErrorBoundary>} />
                <Route path="/request-quote" element={<PageErrorBoundary pageName="Request quote"><RequestQuote /></PageErrorBoundary>} />
                <Route path="/quick-pay" element={<PageErrorBoundary pageName="Quick pay"><QuickPay /></PageErrorBoundary>} />
                <Route path="/book-now" element={<PageErrorBoundary pageName="Book now"><BookNow /></PageErrorBoundary>} />
                <Route path="/portal" element={<ProtectedRoute><VerificationGate><PageErrorBoundary pageName="Portal"><Portal /></PageErrorBoundary></VerificationGate></ProtectedRoute>} />
                <Route path="/book" element={<ProtectedRoute><VerificationGate><PageErrorBoundary pageName="Booking"><BookingPage /></PageErrorBoundary></VerificationGate></ProtectedRoute>} />
                <Route path="/reset-password/:token?" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmailPending />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </Layout>
            </VerificationGuard>
          </HashRouter>
        </PermissionErrorBoundary>
      </ToastContext.Provider>
    </AuthContext.Provider>
  );
};

export default App;
